(function ($, window, document) {
    'use strict';

    /**
     * Configuration object for the application.
     *
     * @property {string} baseUrl The base URL for API requests. Defaults to '/user' if not provided by the WayGenAI object.
     * @property {string} loginUrl The URL for the login endpoint. Defaults to '/user/log-in/' if not provided by the WayGenAI object.
     * @property {string} homeUrl The URL for the home page. Defaults to '/user/' if not provided by the WayGenAI object.
     * @property {Object} i18n Internationalization (i18n) messages for the application.
     * @property {string} i18n.invalidSession Message displayed when the user session is invalid or has expired.
     * @property {string} i18n.checking Message displayed when the application is verifying the user's session status.
     * @property {string} i18n.redirecting Message displayed when the user is being redirected to the login page.
     * @property {string} i18n.usage Label for usage information. Merges values from WayGenAI's i18n object, if available.
     */
    const CFG = {
        baseUrl: window.WayGenAI?.apiBase || '/user',
        loginUrl: window.WayGenAI?.loginUrl || '/user/log-in/',
        homeUrl: window.WayGenAI?.homeUrl || '/user/',
        i18n: Object.assign({
            invalidSession: 'Your session has expired. Please log in again.',
            checking: 'Checking your session…',
            redirecting: 'Redirecting to login…',
            usage: 'Usage'
        }, window.WayGenAI?.i18n || {})
    };

    /**
     * Clears the cached user information used by the site header.
     * @return {void} This method does not return any value.
     */
    function clearCachedUser() {
        try {
            localStorage.removeItem('wg_user');
        } catch (_) {
        }
    }

    /**
     * Escapes HTML special characters in a given string to prevent HTML injection.
     *
     * @param {string} value - The string to escape. If null or undefined, it will be treated as an empty string.
     * @return {string} The escaped string with HTML special characters replaced by their corresponding entities.
     */
    function escHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    /**
     * Redirects to a URL safely by verifying its origin and protocol. Ensures fallback to a default route if the URL is invalid or unsafe.
     *
     * @param {string} url - The URL to which the redirection is intended.
     * @param {Object} [options] - An object containing optional parameters.
     * @param {string} [options.fallback='/user/log-in/'] - The fallback path to return if the provided URL is unsafe or invalid.
     * @return {string} - The validated and safe redirect path, or the fallback URL if the validation fails.
     */
    function safeRedirect(url, {fallback = '/user/log-in/'} = {}) {
        try {
            const base = window.location.origin;
            const u = new URL(typeof url === 'string' ? url : '', base);
            if (u.origin !== base) return fallback;
            if (u.protocol !== 'http:' && u.protocol !== 'https:') return fallback;
            const path = u.pathname.endsWith('/') ? u.pathname : u.pathname + '/';
            return path + u.search;
        } catch {
            return fallback;
        }
    }

    /**
     * Stores the user object into local storage.
     *
     * @param {Object} user - The user object to be stored.
     * @return {void}
     */
    function setUser(user) {
        try {
            localStorage.setItem('wg_user', JSON.stringify(user));
        } catch (_) {
        }
    }

    /**
     * Retrieves the user data stored in local storage under the key 'wg_user'.
     * If the data exists, it parses and returns the JSON object.
     * Returns null if there is no data or an error occurs during parsing.
     *
     * @return {Object|null} The parsed user object if available, or null if not found or on error.
     */
    function getUser() {
        try {
            const raw = localStorage.getItem('wg_user');
            return raw ? JSON.parse(raw) : null;
        } catch (_) {
            return null;
        }
    }

    /**
     * Verifies the cookie-backed session before initializing authenticated pages.
     *
     * @return {jQuery.Deferred.Promise} The session request promise.
     */
    function verifySessionOnLoad() {
        return $.ajax({
            url: CFG.baseUrl + '/me',
            method: 'GET',
            dataType: 'json'
        })
        .done(function (res) {
            setUser(res);
            renderUsage();
            markActiveLink();
        })
        .fail(function () {
            clearCachedUser();
            hardRedirectToLogin();
        })
        .always(function () {
            $('body').removeClass('wg-user-not-loaded');
        });
    }

    /**
     * Redirects the user to the login page. If an element with the ID 'wg-usage-value' exists,
     * it updates the element's text content before performing the redirection. The redirection
     * URL is securely obtained through the `safeRedirect` function using a configuration value.
     *
     * @return {void} Does not return a value.
     */
    function hardRedirectToLogin() {
        try {
            const el = document.getElementById('wg-usage-value');
            if (el) el.textContent = '…';
        } catch (_) {
        }
        window.location.assign(safeRedirect(CFG.loginUrl));
    }

    /**
     * Restricts a number to be within a specified range.
     *
     * @param {number} n - The number to clamp.
     * @param {number} min - The lower boundary of the range.
     * @param {number} max - The upper boundary of the range.
     * @return {number} The clamped value within the range [min, max].
     */
    function clamp(n, min, max) {
        return Math.max(min, Math.min(max, n));
    }

    /**
     * Computes the usage percentage for a user based on the provided energy data.
     *
     * @param {Object} user - The user object containing energy information.
     * @param {Object} [energy=null] - Optional energy object that may contain updated energy percentage.
     * @param {number} [energy.new_energy_percent] - The new energy percentage value to consider.
     * @return {number} The computed usage percentage, clamped between 0 and 100. Returns 0 if the percentage cannot be determined.
     */
    function computeUsagePercent(user, energy = null) {
        let p;
        if (energy && typeof energy.new_energy_percent === 'number') {
            p = energy.new_energy_percent;
        } else {
            p = user?.energy?.energy_percent;
        }
        if (typeof p !== 'number' || isNaN(p)) return 0;
        return clamp(Math.round(p), 0, 100);
    }

    /**
     * Returns a specific color based on the provided percentage range.
     *
     * @param {number} percent - The percentage value determining the color. Expected to be between 0 and 100.
     * @return {string} A string representing a color code in HEX format. The color corresponds to the specified percentage range:
     *                  - 0–20: Red (#d9534f)
     *                  - 21–40: Orange (#f0ad4e)
     *                  - 41–60: Green (#5cb85c)
     *                  - 61–100: Brand color (#3f7d82)
     */
    function colorFor(percent) {
        if (percent <= 20) return '#d9534f';     // 0–20 red
        if (percent <= 40) return '#f0ad4e';     // 21–40 orange
        if (percent <= 60) return '#5cb85c';     // 41–60 green
        return '#3f7d82';                        // 61–100 brand
    }

    /**
     * Updates and renders the usage meter elements in the UI by computing the usage percentage
     * and applying corresponding styles and values.
     *
     * @param {number|null} [energy=null] - The energy value used to compute the usage percentage. If null, a default value may be used.
     * @return {void} Does not return a value.
     */
    function renderUsage(energy = null) {
        const user = getUser();
        const p = computeUsagePercent(user, energy);
        const ring = document.querySelector('.wg-usage__ring');
        const value = document.getElementById('wg-usage-value');
        const label = document.querySelector('.wg-usage__label');
        if (!ring || !value) return;

        const color = colorFor(p);
        const deg = Math.round(p * 3.6);

        ring.style.setProperty('--wg-usage-color', color);
        ring.style.setProperty('--wg-usage-deg', deg + 'deg');
        value.textContent = String(p) + '%';
        if (label) label.textContent = CFG.i18n.usage;
    }

    /**
     * Normalizes a given path by ensuring it does not end with a trailing slash, unless it is the root path.
     * If the input is invalid, it returns the root path.
     *
     * @param {string} p - The input path to be normalized. It should be a valid URL pathname or relative path.
     * @return {string} - The normalized path. Returns the input path without a trailing slash (if applicable) or '/' for invalid inputs.
     */
    function normalizePath(p) {
        try {
            const pathname = new URL(p, window.location.origin).pathname;
            return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
        } catch (e) {
            return '/';
        }
    }

    /**
     * Marks the most relevant link as active by comparing the current path with the links in the sidebar.
     * The function normalizes the current path and matches it against the href attributes of the links.
     * The longest matching link is marked as active, and the active state is removed from other links.
     *
     * @return {void} This function does not return a value.
     */
    function markActiveLink() {
        const current = normalizePath(window.WayGenAI?.currentPath || window.location.pathname);
        const $links = $('.rail__link[href]');
        $links.removeClass('is-active').removeAttr('aria-current');

        let bestEl = null, bestLen = -1;
        $links.each(function () {
            const rawHref = this.getAttribute('href');
            if (!rawHref) return;
            let url;
            try {
                url = new URL(rawHref, window.location.origin);
            } catch {
                return;
            }
            if (url.origin !== window.location.origin) return;
            const linkPath = normalizePath(url.pathname);
            const isMatch = current === linkPath || current.startsWith(linkPath + '/');
            if (isMatch && linkPath.length > bestLen) {
                bestLen = linkPath.length;
                bestEl = this;
            }
        });

        if (bestEl) $(bestEl).addClass('is-active').attr('aria-current', 'page');
    }

    // verify session on load
    verifySessionOnLoad();

    // init
    $(function () {
        initEmailVerifyOverlay();
        initRail();
        renderUsage();
        initMyAccount();
        initMyAccountDeletion();
        initRouteGeneration();
        initRoutes();
        initPlanUsage();
    });

    /**
     * Initializes the rail toggle functionality. This method sets up an event listener
     * on the rail toggle element. When the toggle is clicked, the "wg-rail-collapsed"
     * class is toggled on the document body, and the element's aria-expanded attribute
     * is updated accordingly.
     *
     * @return {void} Does not return a value.
     */
    function initRail() {
        const $toggle = $('#wg-rail-toggle');
        $toggle.on('click', function () {
            const collapsed = document.body.classList.toggle('wg-rail-collapsed');
            $toggle.attr('aria-expanded', String(!collapsed));
        });
    }

    /**
     * Initializes the email verification overlay for the user. The overlay prompts users to verify their email address
     * and allows them to resend a verification email if needed. The method also handles session validation
     * and error handling during the email resend process.
     *
     * @return {void} This method does not return a value.
     */
    function initEmailVerifyOverlay() {
        const $ov = $('#wg-verify-overlay');
        if (!$ov.length) return;

        /**
         * I18N_VER is an object containing localized messages related to verification processes.
         * It is intended to standardize user-facing messaging for various scenarios, such as
         * successful email verification or errors related to session or network issues. The object
         * combines default messages with any additional messages that may be provided via the
         * `verifyI18n` property of `window.WayGenAI`.
         *
         * Properties:
         * - sentOk: String message displayed when a verification email is successfully sent.
         * - sentFail: String message displayed when a verification email could not be sent.
         * - invalidSession: String message displayed when the user's session has expired.
         * - network: String message displayed in the event of a network error.
         */
        const I18N_VER = Object.assign({
            sentOk: 'A new verification email was sent. Please check your inbox and follow the instructions.',
            sentFail: 'Could not send the verification email. Please try again.',
            invalidSession: 'Your session has expired. Please log in again.',
            network: 'Network error. Please try again.'
        }, window.WayGenAI.verifyI18n || {});

        const user = (typeof getUser === 'function') ? getUser() : null;
        // check user verified
        if (!user || user.is_verified === true) {
            return;
        }
        try {
            $('#wg-verify-email span').text(user.email || '');
        } catch (_) {
        }

        $ov.addClass('is-open').show().removeAttr('hidden');

        // resend email
        $(document).off('click.wgResend', '#wg-ver-resend').on('click.wgResend', '#wg-ver-resend', function () {
            const $btn = $('#wg-ver-resend');
            const $sp = $btn.find('.spinner');
            const $ok = $('#wg-ver-success');
            const $err = $('#wg-ver-error');

            $btn.prop('disabled', true);
            $sp.show();
            $ok.hide().text('');
            $err.hide().text('');

            $.ajax({
                url: (CFG.baseUrl || '/user') + '/resend-email-verification',
                method: 'POST',
                dataType: 'json'
            })
            .done(function () {
                $ok.text(I18N_VER.sentOk).show();
                $btn.prop('disabled', false).hide();
            })
            .fail(function (jqXHR) {
                const status = jqXHR.status;
                const detail = jqXHR?.responseJSON?.detail || '';

                if (status === 401) {
                    if (typeof clearCachedUser === 'function') clearCachedUser();
                    $err.text(I18N_VER.invalidSession).show();
                    setTimeout(function () {
                        window.location.assign(typeof safeRedirect === 'function' ? safeRedirect(CFG.loginUrl) : (CFG.loginUrl || '/user/log-in/'));
                    }, 700);
                    return;
                }
                if (status === 400 && /already verified/i.test(detail)) {
                    $ov.removeClass('is-open').hide().attr('hidden', 'hidden');
                    $.ajax({
                        url: (CFG.baseUrl || '/user') + '/me',
                        method: 'GET',
                        dataType: 'json'
                    }).done(function (res) {
                        if (typeof setUser === 'function' && res) setUser(res);
                    });
                    return;
                }
                if (status === 0) {
                    $err.text(I18N_VER.network).show();
                    return;
                }
                $err.text(detail || I18N_VER.sentFail).show();
            })
            .always(function () {
                $btn.prop('disabled', false);
                $sp.hide();
            });
        });

        // recheck email verification status
        $(document).off('click.wgRecheckVerify', '#wg-ver-recheck').on('click.wgRecheckVerify', '#wg-ver-recheck', function () {
            const $btn = $('#wg-ver-recheck');
            const $resendBtn = $('#wg-ver-resend');
            const $sp = $btn.find('.spinner');
            const $ok = $('#wg-ver-success');
            const $err = $('#wg-ver-error');

            $btn.prop('disabled', true);
            $resendBtn.prop('disabled', true);
            $sp.show();
            $ok.hide().text('');
            $err.hide().text('');

            verifySessionOnLoad()
                .done(function () {
                    const freshUser = getUser();
                    if (freshUser && freshUser.is_verified === true) {
                        $ov.removeClass('is-open').hide().attr('hidden', 'hidden');
                    }
                })
                .fail(function () {
                    $err.text(I18N_VER.invalidSession).show();
                })
                .always(function () {
                    $btn.prop('disabled', false);
                    $resendBtn.prop('disabled', false);
                    $sp.hide();
                });
        });
    }

    /**
     * Initializes the account management functionality by rendering the localized string resources,
     * setting up event handlers for form elements, and ensuring interactive functionality,
     * such as toggling password visibility and handling user input validations.
     *
     * The function dynamically populates the account form fields with user information,
     * manages localized messages for errors and successes, and aligns the UI behaviors
     * with user actions related to updating personal details or changing passwords.
     *
     * @return {void} This function does not return a value.
     */
    function initMyAccount() {
        if (!$('.wg-account').length) return;

        /**
         * Internationalization (I18N) object that contains a set of localized strings used for
         * displaying user-facing messages in the application. These strings are primarily
         * related to user account actions and errors.
         *
         * The object allows easy access to pre-defined messages, which can be extended or
         * overridden by an external configuration object, `CFG.myAccountI18n`, if available.
         *
         * Each key in the object represents a specific context or action, and its value
         * corresponds to the localized message.
         *
         * Properties:
         * - `accSaved`: Message displayed when the user profile is successfully updated.
         * - `accSavedPw`: Message displayed upon a successful password change.
         * - `accFail`: Message shown when profile updates fail.
         * - `fnInvalid`: Error message when the first name contains invalid characters.
         * - `lnInvalid`: Error message when the last name contains invalid characters.
         * - `pwCurrentEmpty`: Prompt to ask the user to enter their current password.
         * - `pwCurrentWrong`: Error message when the current password is incorrect.
         * - `pwEmpty`: Prompt to ask the user to enter a password.
         * - `pwWeak`: Error message when the entered password does not meet strength requirements.
         * - `pwMismatch`: Error message when the entered passwords do not match.
         * - `invalidSession`: Message displayed when the user's session has expired.
         */
        const I18N = Object.assign({
            accSaved: 'Profile updated.',
            accSavedPw: 'Password changed.',
            accFail: 'Could not update profile. Please try again.',
            fnInvalid: 'First name contains invalid characters.',
            lnInvalid: 'Last name contains invalid characters.',
            pwCurrentEmpty: 'Please enter your current password.',
            pwCurrentWrong: 'Current password is incorrect.',
            pwEmpty: 'Please enter a password.',
            pwWeak: 'Password does not meet the strength requirements.',
            pwMismatch: 'Passwords do not match.',
            invalidSession: 'Your session has expired. Please log in again.'
        }, CFG.myAccountI18n || {});

        /**
         * Displays an account-related error message in the global error section
         * and hides the success message section.
         *
         * @param {string} msg - The error message to be displayed. If not provided, a default localized error message will be used.
         * @return {void}
         */
        function showAccError(msg) {
            $('#acc-global-error').show().find('span').text(msg || I18N.accFail);
            $('#acc-success').hide().find('span').text('');
        }

        /**
         * Displays a success message inside the account success element and hides any global error message.
         *
         * @param {string} [msg] - The message to display in the account success element. If not provided, a default message will be used.
         * @return {void} This method does not return a value.
         */
        function showAccSuccess(msg) {
            $('#acc-success').show().find('span').text(msg || I18N.accSaved);
            $('#acc-global-error').hide().find('span').text('');
        }

        /**
         * Clears all account-related error and success messages from the user interface.
         *
         * This function hides the global error and success message containers, clears their text,
         * removes any specific field error messages, and removes the invalid input class from
         * input elements.
         *
         * @return {void} Doesn't return anything.
         */
        function clearAccErrors() {
            $('#acc-global-error').hide().find('span').text('');
            $('#acc-success').hide().find('span').text('');
            $('.field-error[data-error-for]').text('');
            $('.input').removeClass('is-invalid');
        }

        /**
         * Toggles the loading state of a button by enabling/disabling it and showing/hiding a spinner.
         *
         * @param {jQuery} $btn - The button element wrapped in a jQuery object.
         * @param {boolean} on - A boolean value indicating whether to activate (true) or deactivate (false) the loading state.
         * @return {void} This function does not return a value.
         */
        function setBtnLoading($btn, on) {
            $btn.prop('disabled', on);
            $btn.find('.spinner').toggle(on);
        }

        /**
         * Checks if the provided value is a valid name.
         *
         * A valid name contains only word characters, whitespace, hyphens, and apostrophes.
         * Returns true for an empty value.
         *
         * @param {string} v - The value to be checked.
         * @return {boolean} True if the value is valid or empty, otherwise false.
         */
        function isValidName(v) {
            if (!v) return true;
            return /^[\w\s\-']+$/u.test(String(v).trim());
        }

        /**
         * Checks if a given password meets the criteria for being considered strong.
         * A strong password must be at least 8 characters long, contain at least one lowercase letter,
         * one uppercase letter, one numeric digit, and one special character. It must also consist only
         * of valid characters (letters, numbers, and typical special characters).
         *
         * @param {string} pw The password to be validated.
         * @return {boolean} Returns true if the password is strong, otherwise returns false.
         */
        function isStrongPassword(pw) {
            if (!pw || pw.length < 8) return false;
            if (!/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,<.>\/\?`~]+$/.test(pw)) return false;
            if (!/[a-z]/.test(pw)) return false;
            if (!/[A-Z]/.test(pw)) return false;
            if (!/[0-9]/.test(pw)) return false;
            if (!/[^a-zA-Z0-9]/.test(pw)) return false;
            return true;
        }

        /**
         * Populates the account form with user data and adjusts UI elements based on the user's attributes.
         *
         * The method retrieves the current user data, updates form fields with the user's information,
         * and modifies the display of certain components based on the user's subscription and
         * vendor details.
         *
         * @return {void} Does not return any value.
         */
        function hydrateAccountForm() {
            const user = getUser();
            if (!user) return;

            $('#wg-account-email').find('span').text(user.email);

            $('#acc-first').val(user.first_name || '');
            $('#acc-last').val(user.last_name || '');
            if (typeof user.news_subscribe === 'boolean') {
                $('#consent-news').prop('checked', !!user.news_subscribe);
            }

            const vendorName = user.vendor?.vendor || '';
            const isWebsiteUser = !vendorName || vendorName === 'website';
            if (!isWebsiteUser) {
                $('#acc-toggle-pw').hide();
                $('#acc-pw-block').hide();
            }
        }

        // change password
        $(document).on('click', '#acc-toggle-pw', function () {
            const box = $('#acc-pw-block');
            box.toggle();
            if (!box.is(':visible')) {
                $('#acc-pw-current,#acc-pw-new,#acc-pw-new2').val('');
                $('.field-error[data-error-for^="acc-pw-"]').text('');
                $('#acc-pw-current,#acc-pw-new,#acc-pw-new2').removeClass('is-invalid');
            } else {
                $('#acc-pw-current').trigger('focus');
            }
        });

        // reset errors on fields changes
        $(document).on('input', '#acc-first,#acc-last,#acc-pw-current,#acc-pw-new,#acc-pw-new2,#consent-news', function () {
            const id = this.id;
            $(`[data-error-for="${id}"]`).text('');
            $('#' + id).removeClass('is-invalid');
            $('#acc-global-error').hide().text('');
        });

        // toggle password visibility (works for any .pw-toggle next to an <input>)
        $(document).on('click', '.pw-toggle', function () {
            const $btn = $(this);
            const $input = $btn.siblings('input');
            if (!$input.length) return;
            const isHidden = $input.attr('type') === 'password';
            $input.attr('type', isHidden ? 'text' : 'password');
            const $icon = $btn.find('i');
            $icon.toggleClass('fa-eye', isHidden).toggleClass('fa-eye-slash', !isHidden);
        });

        // save account changes
        $(document).on('submit', '#wg-account-form', function (e) {
            e.preventDefault();
            clearAccErrors();
            const $btn = $('#acc-save');
            setBtnLoading($btn, true);

            const first = $('#acc-first').val().trim();
            const last = $('#acc-last').val().trim();
            const news = $('#consent-news').is(':checked');

            const pwVisible = $('#acc-pw-block').is(':visible');
            const pwCurrent = $('#acc-pw-current').val();
            const pwNew = $('#acc-pw-new').val();
            const pwNew2 = $('#acc-pw-new2').val();
            const wantsPasswordChange = pwVisible && !!(pwCurrent || pwNew || pwNew2);

            let hasError = false;
            if (!isValidName(first)) {
                $('[data-error-for="acc-first"]').text(I18N.fnInvalid);
                $('#acc-first').addClass('is-invalid');
                hasError = true;
            }
            if (!isValidName(last)) {
                $('[data-error-for="acc-last"]').text(I18N.lnInvalid);
                $('#acc-last').addClass('is-invalid');
                hasError = true;
            }

            let newPasswordToSend = null;
            if (wantsPasswordChange) {
                if (!pwCurrent) {
                    $('[data-error-for="acc-pw-current"]').text(I18N.pwCurrentEmpty);
                    $('#acc-pw-current').addClass('is-invalid');
                    hasError = true;
                }

                if (!pwNew) {
                    $('[data-error-for="acc-pw-new"]').text(I18N.pwEmpty);
                    $('#acc-pw-new').addClass('is-invalid');
                    hasError = true;
                } else {
                    if (!isStrongPassword(pwNew)) {
                        $('[data-error-for="acc-pw-new"]').text(I18N.pwWeak);
                        $('#acc-pw-new').addClass('is-invalid');
                        hasError = true;
                    }
                    if (pwNew !== pwNew2) {
                        $('[data-error-for="acc-pw-new2"]').text(I18N.pwMismatch);
                        $('#acc-pw-new2').addClass('is-invalid');
                        hasError = true;
                    }
                }

                if (!hasError) {
                    newPasswordToSend = pwNew;
                }
            }

            if (hasError) {
                setBtnLoading($btn, false);
                return;
            }

            const payload = {};
            const user = getUser();
            if (first && first !== (user?.first_name || '')) payload.first_name = first;
            if (last && last !== (user?.last_name || '')) payload.last_name = last;
            if (typeof news === 'boolean' && news !== !!(user?.news_subscribe)) payload.news_subscribe = !!news;
            if (newPasswordToSend) {
                payload.old_password = pwCurrent;
                payload.password = newPasswordToSend;
            }

            if (Object.keys(payload).length === 0) {
                setBtnLoading($btn, false);
                showAccSuccess(I18N.accSaved);
                return;
            }

            // set separate loader
            $('body').addClass('wg-request-in-progress');

            $.ajax({
                url: CFG.baseUrl + '/me',
                method: 'PATCH',
                contentType: 'application/json',
                dataType: 'json',
                data: JSON.stringify(payload)
            })
            .done(function (res) {
                if (res?.user) setUser(res.user);
                showAccSuccess(newPasswordToSend ? I18N.accSavedPw : I18N.accSaved);
                if (wantsPasswordChange) {
                    $('#acc-toggle-pw').trigger('click');
                }
                $('#acc-pw-current,#acc-pw-new,#acc-pw-new2').val('');
            })
            .fail(function (jqXHR) {
                const status = jqXHR.status;
                const resp = jqXHR.responseJSON;
                if (status === 401) {
                    clearCachedUser();
                    showAccError(I18N.invalidSession);
                    setTimeout(function () {
                        window.location.assign(safeRedirect(CFG.loginUrl));
                    }, 600);
                    return;
                }
                if (status === 400) {
                    if (resp?.detail === 'Current password is required') {
                        $('[data-error-for="acc-pw-current"]').text(I18N.pwCurrentEmpty);
                        $('#acc-pw-current').addClass('is-invalid');
                    } else if (resp?.detail === 'Current password is incorrect') {
                        $('[data-error-for="acc-pw-current"]').text(I18N.pwCurrentWrong);
                        $('#acc-pw-current').addClass('is-invalid');
                    } else {
                        showAccError(resp?.detail || I18N.accFail);
                    }
                    return;
                }
                if (status === 422 && Array.isArray(resp?.detail)) {
                    resp.detail.forEach(function (err) {
                        const loc = (err.loc || []).slice(-1)[0];
                        const msg = err.msg || I18N.accFail;
                        if (loc === 'first_name') {
                            $('[data-error-for="acc-first"]').text(msg);
                            $('#acc-first').addClass('is-invalid');
                        } else if (loc === 'last_name') {
                            $('[data-error-for="acc-last"]').text(msg);
                            $('#acc-last').addClass('is-invalid');
                        } else if (loc === 'password') {
                            $('[data-error-for="acc-pw-new"]').text(msg);
                            $('#acc-pw-new').addClass('is-invalid');
                        } else {
                            showAccError(msg);
                        }
                    });
                    return;
                }
                showAccError(resp?.detail || I18N.accFail);
            })
            .always(function () {
                setBtnLoading($btn, false);
                $('body').removeClass('wg-request-in-progress');
            });
        });

        // init form
        $(function () {
            hydrateAccountForm();
        });
    }

    /**
     * Initializes the "My Account Deletion" process by setting up necessary event handlers
     * and configuring localized text for the deletion overlay. This method includes setup
     * for internationalized messages, user confirmation workflows, error handling, and
     * seamless integration for token-based sessions. It ensures that the user experience
     * is intuitive and localized during the account deletion process.
     *
     * @return {void} Does not return a value.
     */
    function initMyAccountDeletion() {
        if (!$('.wg-account').length) return;

        /**
         * I18N_DEL is an object containing internationalized strings used for the account deletion process.
         * The object provides default messages for various steps and possible error scenarios encountered
         * during the deletion workflow. Custom translations can be merged into this object via an external
         * configuration, ensuring localization support.
         *
         * Properties:
         * - delCancel: Message shown when a user decides to cancel the account deletion process.
         * - delConfirm: Message displayed as confirmation when a user agrees to delete their account.
         * - delFail: Error message shown when the account deletion process fails.
         * - delNetwork: Error message displayed in case of a network-related issue during deletion.
         * - delInvalidSession: Message indicating that the user's session has expired and they need to log in again.
         * - delRedirecting: Message indicating that the account deletion process is underway and redirection is occurring.
         */
        const I18N_DEL = Object.assign({
            delCancel: 'I changed my mind',
            delConfirm: 'Yes, delete my account',
            delFail: 'Could not delete the account. Please try again.',
            delNetwork: 'Network error. Please try again.',
            delInvalidSession: 'Your session has expired. Please log in again.',
            delRedirecting: 'Deleting… Redirecting…'
        }, CFG.myAccountDeletionI18n || {});

        /**
         * Updates the text for delete overlay buttons with localized values.
         * This method sets the cancel and confirm button text for a delete overlay
         * based on provided internationalization (I18N) data.
         *
         * @return {void} Does not return a value.
         */
        function hydrateDeleteOverlayTexts() {
            $('#wg-del-cancel').text(I18N_DEL.delCancel);
            $('#wg-del-confirm').text(I18N_DEL.delConfirm);

            const user = getUser();
            const vendorName = user?.vendor?.vendor || '';

            /**
             * Capitalizes the first character of the given string.
             *
             * @param {string} s - The string to capitalize.
             * @return {string} The string with the first character converted to uppercase.
             */
            function capitalize(s) {
                return s && String(s[0]).toUpperCase() + String(s).slice(1);
            }

            $('#wg-del-note-social').html((_, html) => html.replace('{VENDOR}', capitalize(vendorName))).show();
        }

        /**
         * Opens and displays the delete overlay modal. Adjusts the content of the modal based on the user's vendor type.
         *
         * @return {void} Does not return a value.
         */
        function openDeleteOverlay() {
            const $ov = $('#wg-delete-overlay');
            if (!$ov.length) return;
            const user = getUser();
            const vendorName = user?.vendor?.vendor || '';
            if (vendorName && vendorName !== 'website') {
                $('#wg-del-note-social').show();
                $('#wg-del-note-website').hide();
            } else {
                $('#wg-del-note-social').hide();
                $('#wg-del-note-website').show();
            }
            $('#wg-del-error').hide().text('');
            hydrateDeleteOverlayTexts();
            $ov.addClass('is-open').show().removeAttr('hidden');
        }

        /**
         * Closes the delete overlay by removing the 'is-open' class, hiding the overlay,
         * setting the 'hidden' attribute, and clearing any displayed error message.
         *
         * @return {void} No return value.
         */
        function closeDeleteOverlay() {
            const $ov = $('#wg-delete-overlay');
            $ov.removeClass('is-open').hide().attr('hidden', 'hidden');
            $('#wg-del-error').hide().text('');
        }

        $(document).on('click', '#acc-delete', openDeleteOverlay);
        $(document).on('click', '#wg-del-cancel, [data-del-close]', closeDeleteOverlay);

        // close on escape
        $(document).on('keydown', function (e) {
            if (e.key === 'Escape') closeDeleteOverlay();
        });

        // delete account
        $(document).on('click', '#wg-del-confirm', function () {
            const $btn = $('#wg-del-confirm');
            $btn.prop('disabled', true);
            $('#wg-del-error').hide().text('');

            // set separate loader
            $('body').addClass('wg-request-in-progress');

            $.ajax({
                url: CFG.baseUrl + '/me',
                method: 'DELETE'
            })
            .done(function () {
                try {
                    localStorage.removeItem('wg_user');
                } catch (_) {
                }
                $('#wg-del-error').hide().text('');
                window.location.assign(safeRedirect(CFG.homeUrl));
            })
            .fail(function (jqXHR) {
                const status = jqXHR.status;
                if (status === 401) {
                    clearCachedUser();
                    $('#wg-del-error').text(I18N_DEL.delInvalidSession).show();
                    setTimeout(function () {
                        window.location.assign(safeRedirect(CFG.loginUrl));
                    }, 700);
                    return;
                }
                if (status === 0) {
                    $('#wg-del-error').text(I18N_DEL.delNetwork).show();
                    return;
                }
                const msg = jqXHR?.responseJSON?.detail || I18N_DEL.delFail;
                $('#wg-del-error').text(msg).show();
            })
            .always(function () {
                $btn.prop('disabled', false);
                $('body').removeClass('wg-request-in-progress');
            });
        });
    }

    /**
     * Initializes the route generation process by configuring necessary elements, setting up internationalized messaging, and managing dependencies.
     * Performs setup tasks like creating hidden input fields, error handling, and defining utility methods for route-related functionalities.
     *
     * @return {void} This method does not return a value.
     */
    function initRouteGeneration() {
        if (!$('.wg-route').length && !$('.wg-my-routes').length) return;

        /**
         * A dictionary of internationalized strings used for route generation messaging.
         * This object contains default messages and merges any additional strings defined in `window.WayGenAI.routeGenerationI18N`.
         *
         * Properties:
         * - `placeholder`: Prompt text shown in dropdowns for choosing a country.
         * - `needQuery`: Message displayed when a full address is required before proceeding.
         * - `needCountry`: Message displayed when a country must be selected before proceeding.
         * - `needMode`: Message displayed when a mode of operation must be selected.
         * - `needStyle`: Message displayed when a style must be selected.
         * - `needPrompt`: Request message prompting the user to add additional details.
         * - `needOrigin`: Message prompting the user to confirm the starting location either via search or location detection.
         * - `geoNotSupported`: Message displayed when geolocation is not supported by the user's browser.
         * - `invalidSession`: Message displayed when the user's session has expired, prompting them to log in.
         * - `network`: Error message displayed when a network issue occurs.
         * - `healthBusyNotice`: Notification that all workers are currently busy, which may cause delays in route generation.
         * - `generating`: Array of strings representing different stages or statuses during the route generation process.
         */
        const routeGenerationI18N = Object.assign({
            placeholder: 'Choose a country',
            needQuery: 'Please type a full address first',
            needCountry: 'Please choose a country',
            needMode: 'Please choose a mode',
            needStyle: 'Please choose a style',
            needPrompt: 'Please add some details',
            needOrigin: 'Please confirm your start location (search or locate).',
            geoNotSupported: 'Geolocation is not supported by your browser',
            invalidSession: 'Your session has expired. Please log in again.',
            network: 'Network error. Please try again.',
            healthBusyNotice: 'All workers are busy right now — generation may take longer.',
            generating: ['Thinking…', 'Planning your route…', 'Picking best options…', 'Optimizing segments…']
        }, window.WayGenAI?.routeGenerationI18N || {});
        const GEOCODE_API = window.WayGenAI?.apiGeocodeBase || '/start/geocode';
        const START_API = window.WayGenAI?.apiStartBase || '/start';
        const DECLINE_START_API = window.WayGenAI?.apiDeclineStartBase || '/use_route/decline_start';
        const HEALTH_API = window.WayGenAI?.apiHealth || '/health';

        /**
         * Handles HTTP 401 Unauthorized errors by performing necessary actions such as
         * clearing the authentication token and redirecting the user to the login page.
         * The function behavior is dependent on the availability of certain external
         * methods and properties.
         *
         * @return {void} Does not return a value.
         */
        function handle401() {
            if (typeof clearCachedUser === 'function') clearCachedUser();
            if (typeof hardRedirectToLogin === 'function') {
                hardRedirectToLogin();
            } else {
                window.location.assign(window.WayGenAI?.loginUrl || '/user/log-in/');
            }
        }

        /**
         * Displays an error message in the designated error element on the page.
         * The error message is displayed in an element with the ID "gen-error".
         * If no message is provided, it clears any existing text and shows the element.
         *
         * @param {string} msg - The error message to display. If undefined or empty, the element is shown without text.
         * @return {void} This function does not return a value.
         */
        function showError(msg) {
            $('#gen-error').text(msg || '').show();
        }

        /**
         * Clears the error message displayed in the element with the ID 'gen-error'.
         * This function hides the element and removes any text content within it.
         *
         * @return {void} No return value.
         */
        function clearError() {
            $('#gen-error').hide().text('');
        }

        /**
         * Toggles the 'loading' class on the specified element.
         *
         * @param {HTMLElement | string} el - The DOM element or a selector for the element to toggle the class on.
         * @param {boolean} [on=true] - Determines whether to add or remove the 'loading' class. Defaults to true.
         * @return {void} Does not return a value.
         */
        function setLoading(el, on = true) {
            $(el).toggleClass('loading', !!on);
        }

        /**
         * Sets the loading state of a button by enabling or disabling it and toggling a spinner within the button.
         *
         * @param {jQuery} $btn - The jQuery-wrapped button element to modify.
         * @param {boolean} [on=true] - A boolean indicating whether the button should be set to the loading state. Default is true.
         * @return {void} This function does not return any value.
         */
        function setBtnLoading($btn, on = true) {
            $btn.prop('disabled', !!on);
            $btn.find('.spinner').toggle(!!on);
            if ($btn.hasClass('btn-primary')) {
                $btn.toggleClass('loading', !!on);
            }
        }

        const $form = $('#wg-gen-form');
        const $locationInput = $('#gen-location');
        const $countrySelect = $('#gen-country');
        const $locateBtn = $('#gen-locate');
        const $searchBtn = $('#gen-search');
        const $suggestions = $('#gen-location-suggestions');
        const $genBtn = $('#gen-start');
        const $mode = $('#gen-mode');
        const $style = $('#gen-style');
        const $prompt = $('#gen-prompt');

        // origin
        let $originHidden = $('#gen-origin-json');
        if (!$originHidden.length) {
            $originHidden = $('<input>', {type: 'hidden', id: 'gen-origin-json', name: 'gen_origin_json'});
            $form.append($originHidden);
        }

        /**
         * Sets the origin by serializing the provided object and storing it in a hidden field.
         *
         * @param {Object} obj - The object to be serialized and set as the origin. Defaults to an empty object if not provided.
         * @return {void} This method does not return a value.
         */
        function setOrigin(obj) {
            try {
                $originHidden.val(JSON.stringify(obj || {}));
            } catch (_) {
                $originHidden.val('');
            }
        }

        /**
         * Extracts and parses the origin data from a hidden input element.
         *
         * The method retrieves the value of a hidden input field, trims it, and attempts to parse it as JSON.
         * It validates whether the parsed object contains numeric latitude (`lat`), numeric longitude (`lon`),
         * and a `country` property. If valid, the parsed object is returned; otherwise, it returns null.
         *
         * @return {Object|null} The parsed origin object with `lat`, `lon`, and `country` properties if valid; otherwise null.
         */
        function getOrigin() {
            try {
                const raw = ($originHidden.val() || '').trim();
                if (!raw) return null;
                const o = JSON.parse(raw);
                if (o && typeof o.lat === 'number' && typeof o.lon === 'number' && o.country) return o;
                return null;
            } catch (_) {
                return null;
            }
        }

        /**
         * Clears the value of the $originHidden element.
         *
         * @return {void} Does not return any value.
         */
        function clearOrigin() {
            $originHidden.val('');
        }

        /**
         * Retrieves the local time and the current weekday.
         *
         * @return {Object} An object containing:
         *                  - time: A string representing the local time in HH:mm format.
         *                  - week_day: An integer (1-7) representing the weekday, where Monday is 1 and Sunday is 7.
         */
        function getLocalTimeAndWeekday() {
            const d = new Date();
            const hh = String(d.getHours()).padStart(2, '0');
            const mm = String(d.getMinutes()).padStart(2, '0');
            const jsDay = d.getDay(); // 0..6, sun=0
            const week_day = ((jsDay + 6) % 7) + 1; // 1..7, mon=1
            return {time: `${hh}:${mm}`, week_day};
        }

        // reset origin on change
        $locationInput.on('input', clearOrigin);
        $countrySelect.on('change', clearOrigin);

        // init country select2
        (function initCountrySelect() {
            if (!$countrySelect.length) return;
            try {
                $countrySelect.select2({
                    placeholder: routeGenerationI18N.placeholder,
                    allowClear: true,
                    width: 'resolve'
                });
                const inst = $countrySelect.data('select2');
                if (inst && inst.$dropdown) inst.$dropdown.addClass('wg-country-dropdown');
            } catch (_) {
            }
        })();

        let overlayTimer = null, overlayMsgIdx = 0;

        /**
         * Ensures and retrieves the overlay element.
         *
         * @return {jQuery} The jQuery object representing the overlay element with the ID 'wg-start-overlay'.
         */
        function ensureOverlay() {
            return $('#wg-start-overlay');
        }

        /**
         * Cycles through a list of messages and updates the overlay message displayed on the UI.
         * This function ensures that the overlay message is updated cyclically
         * based on the array of messages provided. If no array is available, a default message is used.
         *
         * @return {void} Does not return a value, updates the UI element directly.
         */
        function cycleMessages() {
            const arr = Array.isArray(routeGenerationI18N.generating) && routeGenerationI18N.generating.length
                ? routeGenerationI18N.generating : ['Working…'];
            overlayMsgIdx = (overlayMsgIdx + 1) % arr.length;
            $('#wg-start-overlay-msg').text(arr[overlayMsgIdx]);
        }

        /**
         * Displays an overlay on the screen, optionally including an extra note.
         * It also cycles messages defined for the overlay and manages display timing.
         *
         * @param {string} [extraNote] - An optional additional note to display on the overlay.
         *                               If provided, it will be shown; otherwise, it will be hidden.
         * @return {void} This function does not return any value.
         */
        function showOverlay(extraNote) {
            const $ov = ensureOverlay();

            if (overlayTimer) {
                clearInterval(overlayTimer);
                overlayTimer = null;
            }

            overlayMsgIdx = -1;
            cycleMessages();
            if (extraNote) {
                $('#wg-start-overlay-note').text(extraNote).show();
            } else {
                $('#wg-start-overlay-note').hide().text('');
            }
            $ov.fadeIn(120);
            overlayTimer = setInterval(cycleMessages, 5000);
            $('html, body').addClass('no-scroll');
        }

        /**
         * Hides the overlay element by fading it out, clearing any active overlay timer,
         * and re-enabling scrolling on the page.
         *
         * @return {void} No return value.
         */
        function hideOverlay() {
            const $ov = ensureOverlay();
            $ov.fadeOut(120);
            if (overlayTimer) {
                clearInterval(overlayTimer);
                overlayTimer = null;
            }
            $('html, body').removeClass('no-scroll');
        }

        // overlay processer
        window.WGOverlay = {
            show: showOverlay,
            hide: hideOverlay,
            setNote(txt) {
                $('#wg-start-overlay-note').text(txt || '').toggle(!!txt);
            }
        };

        // geolocation
        $locateBtn.on('click', function () {
            if (!navigator.geolocation) {
                alert(routeGenerationI18N.geoNotSupported);
                return;
            }
            clearError();
            setLoading($locationInput, true);
            $countrySelect.prop('disabled', true);
            setLoading($locateBtn, true);
            setLoading($searchBtn, true);
            setBtnLoading($genBtn, true);

            navigator.geolocation.getCurrentPosition(function (pos) {
                $.ajax({
                    url: GEOCODE_API + '/reverse',
                    method: 'POST',
                    contentType: 'application/json',
                    dataType: 'json',
                    data: JSON.stringify({lat: pos.coords.latitude, lon: pos.coords.longitude})
                })
                .done(function (res) {
                    if (res?.ok && Array.isArray(res.results) && res.results.length) {
                        const loc = res.results[0] || {};
                        if (loc.name) $locationInput.val(loc.name);
                        if (loc.country) $countrySelect.val(loc.country).trigger('change');
                        if (typeof loc.lat === 'number' && typeof loc.lon === 'number') {
                            setOrigin({
                                name: loc.name || '',
                                lat: loc.lat,
                                lon: loc.lon,
                                country: (loc.country || '').toUpperCase()
                            });
                        } else {
                            clearOrigin();
                        }
                        renderUsage(res?.energy);
                    } else {
                        clearOrigin();
                    }
                })
                .fail(function (jqXHR) {
                    clearOrigin();
                    if (jqXHR.status === 401) handle401();
                })
                .always(function () {
                    setLoading($locationInput, false);
                    $countrySelect.prop('disabled', false);
                    setLoading($locateBtn, false);
                    setLoading($searchBtn, false);
                    setBtnLoading($genBtn, false);
                });
            }, function () {
                setLoading($locationInput, false);
                $countrySelect.prop('disabled', false);
                setLoading($locateBtn, false);
                setLoading($searchBtn, false);
                setBtnLoading($genBtn, false);
            }, {enableHighAccuracy: true, timeout: 12000, maximumAge: 60000});
        });

        // search address
        $searchBtn.on('click', function () {
            const q = $.trim($locationInput.val());
            const cc = ($countrySelect.val() || '').toString().toUpperCase();
            if (!q) {
                alert(routeGenerationI18N.needQuery);
                return;
            }
            if (!cc) {
                alert(routeGenerationI18N.needCountry);
                return;
            }

            clearError();
            setLoading($locationInput, true);
            $countrySelect.prop('disabled', true);
            setLoading($locateBtn, true);
            setLoading($searchBtn, true);
            setBtnLoading($genBtn, true);
            $suggestions.hide().empty();

            $.ajax({
                url: GEOCODE_API + '/search',
                method: 'POST',
                contentType: 'application/json',
                dataType: 'json',
                data: JSON.stringify({query: q, country: cc, limit: 5})
            })
            .done(function (res) {
                if (res?.ok && Array.isArray(res.results) && res.results.length) {
                    res.results.forEach(function (r) {
                        $('<div/>', {
                            role: 'option',
                            tabindex: 0,
                            text: r.name + (r.country ? ' (' + r.country + ')' : '')
                        })
                            .on('click keydown', function (e) {
                                if (e.type === 'click' || e.key === 'Enter') {
                                    $locationInput.val(r.name || '');
                                    if (r.country) $countrySelect.val(r.country).trigger('change');
                                    if (typeof r.lat === 'number' && typeof r.lon === 'number') {
                                        setOrigin({
                                            name: r.name || '',
                                            lat: r.lat,
                                            lon: r.lon,
                                            country: (r.country || cc).toUpperCase()
                                        });
                                    } else {
                                        clearOrigin();
                                    }
                                    $suggestions.hide().empty();
                                }
                            })
                            .appendTo($suggestions);
                    });
                    $suggestions.show();
                    renderUsage(res?.energy);
                } else {
                    $suggestions.hide();
                    clearOrigin();
                }
            })
            .fail(function (jqXHR) {
                clearOrigin();
                if (jqXHR.status === 401) {
                    handle401();
                    return;
                }
                if (jqXHR.status === 0) alert(routeGenerationI18N.network);
                $suggestions.hide();
            })
            .always(function () {
                setLoading($locationInput, false);
                $countrySelect.prop('disabled', false);
                setLoading($locateBtn, false);
                setLoading($searchBtn, false);
                setBtnLoading($genBtn, false);
            });
        });

        // close suggestions
        $(document).on('click', function (e) {
            if (!$suggestions.length) return;
            if (!$(e.target).closest($suggestions).length &&
                e.target !== $locationInput[0] &&
                e.target !== $searchBtn[0]) {
                $suggestions.hide();
            }
        });

        // text limit counter
        (function initPromptCounter() {
            const $left = $('#gen-prompt-left');
            if (!$left.length) return;

            function tick() {
                const max = Number($prompt.attr('maxlength') || 500);
                const left = Math.max(0, max - ($prompt.val() || '').length);
                $left.text(left);
            }

            $prompt.on('input', tick);
            tick();
        })();

        (/**
         * Initializes the configuration for the bubble options UI component.
         * This setup includes handling mode and style toggles, managing active states,
         * and filtering available styles based on the currently selected mode.
         *
         * @return {void} This method does not return a value.
         */
        function initBubbleOptions() {
            const $modeInput = $('#gen-mode');
            const $styleInput = $('#gen-style');
            const $modeGroup = $('[data-bubble-group="mode"]');
            const $styleGroup = $('[data-bubble-group="style"]');

            /**
             * Updates the state of bubbles in a given group to mark one as active.
             *
             * @param {jQuery} $group - The jQuery object representing the group of bubbles.
             * @param {string} value - The value of the bubble to set as active.
             * @return {void} This method does not return a value.
             */
            function setBubbleActive($group, value) {
                $group.find('.wg-bubble')
                    .removeClass('is-active')
                    .attr('aria-pressed', 'false');

                $group.find(`.wg-bubble[data-value="${value}"]`)
                    .addClass('is-active')
                    .attr('aria-pressed', 'true');
            }

            /**
             * Filters and updates the styles of elements based on the provided mode.
             * Toggles the visibility of elements based on their associated modes and
             * updates the active style if the current style is not available.
             *
             * @param {string} mode - The mode used to filter the styles. Elements not matching this mode will be hidden.
             * @return {void} This function does not return a value.
             */
            function filterStylesForMode(mode) {
                const currentStyle = $styleInput.val();
                let firstAvailable = null;
                let currentStillAvailable = false;

                $styleGroup.find('.wg-bubble').each(function () {
                    const $bubble = $(this);
                    const modes = String($bubble.data('for') || '').split(/\s+/);
                    const isAvailable = modes.includes(mode);

                    $bubble.toggleClass('is-hidden', !isAvailable);

                    if (isAvailable && !firstAvailable) {
                        firstAvailable = String($bubble.data('value') || '');
                    }

                    if (isAvailable && String($bubble.data('value')) === currentStyle) {
                        currentStillAvailable = true;
                    }
                });

                const nextStyle = currentStillAvailable ? currentStyle : firstAvailable;

                if (nextStyle) {
                    $styleInput.val(nextStyle);
                    setBubbleActive($styleGroup, nextStyle);
                }
            }

            $modeGroup.on('click', '.wg-bubble', function () {
                const value = String($(this).data('value') || '');

                if (!value) return;

                $modeInput.val(value);
                setBubbleActive($modeGroup, value);
                filterStylesForMode(value);
            });

            $styleGroup.on('click', '.wg-bubble', function () {
                const $bubble = $(this);

                if ($bubble.hasClass('is-hidden')) return;

                const value = String($bubble.data('value') || '');

                if (!value) return;

                $styleInput.val(value);
                setBubbleActive($styleGroup, value);
            });

            filterStylesForMode($modeInput.val() || 'driving');
        })();

        (/**
         * Initializes the help modal functionality, allowing users to open and close the modal
         * and switch between different help panels based on the context.
         *
         * The modal is displayed when elements with a specific help context are clicked, and it can be
         * closed by clicking the close button, the backdrop, or pressing the Escape key.
         *
         * @return {void} Does not return a value.
         */
        function initHelpModal() {
            const $modal = $('#wg-help-modal');
            const $panels = $modal.find('[data-help-panel]');

            /**
             * Opens a modal dialog and displays the corresponding help panel based on the provided type.
             *
             * @param {string} type - The type of help panel to display within the modal.
             * @return {void} This function does not return a value.
             */
            function openModal(type) {
                $panels.prop('hidden', true);
                $modal.find(`[data-help-panel="${type}"]`).prop('hidden', false);

                $modal.addClass('is-open').attr('aria-hidden', 'false');
                $('html, body').addClass('no-scroll');
            }

            /**
             * Closes the modal by performing the following actions:
             * - Removes the 'is-open' class from the modal element.
             * - Sets the 'aria-hidden' attribute of the modal to 'true'.
             * - Hides all associated panels by setting their 'hidden' property to true.
             * - Removes the 'no-scroll' class from the 'html' and 'body' elements to restore page scrolling.
             *
             * @return {void} This method does not return a value.
             */
            function closeModal() {
                $modal.removeClass('is-open').attr('aria-hidden', 'true');
                $panels.prop('hidden', true);
                $('html, body').removeClass('no-scroll');
            }

            $('.wg-help-btn').on('click', function () {
                openModal($(this).data('help'));
            });

            $('.wg-help-modal__close, .wg-help-modal__backdrop').on('click', closeModal);

            $(document).on('keydown', function (e) {
                if (e.key === 'Escape' && $modal.hasClass('is-open')) {
                    closeModal();
                }
            });
        })();

        // generate route
        $form.on('submit', function (e) {
            e.preventDefault();
            clearError();

            // checking
            const addr = $.trim($locationInput.val());
            const country = ($countrySelect.val() || '').toString().toUpperCase();
            const mode = ($mode.val() || '').toString();
            const style = ($style.val() || '').toString();
            const userQ = $.trim($prompt.val() || '');
            const origin = getOrigin();

            if (!addr) {
                showError(routeGenerationI18N.needQuery);
                return;
            }
            if (!country) {
                showError(routeGenerationI18N.needCountry);
                return;
            }
            if (!mode) {
                showError(routeGenerationI18N.needMode);
                return;
            }
            if (!style) {
                showError(routeGenerationI18N.needStyle);
                return;
            }
            if (!userQ) {
                showError(routeGenerationI18N.needPrompt);
                return;
            }
            if (!origin) {
                showError(routeGenerationI18N.needOrigin);
                return;
            }

            origin.country = country;

            setLoading($locationInput, true);
            $countrySelect.prop('disabled', true);
            setLoading($locateBtn, true);
            setLoading($searchBtn, true);
            setBtnLoading($genBtn, true);

            // check busy workers
            $.ajax({
                url: HEALTH_API,
                method: 'GET',
                dataType: 'json'
            })
            .always(function (jsonOrXHR) {
                try {
                    const j = (jsonOrXHR && jsonOrXHR.responseJSON) ? jsonOrXHR.responseJSON : jsonOrXHR;
                    const free = Number(j?.capacity?.free);
                    const isUnknown = Number.isNaN(free);
                    const isBusy = free <= 0;
                    const note = (isBusy || isUnknown) ? routeGenerationI18N.healthBusyNotice : '';
                    if (window.WGOverlay) window.WGOverlay.show(note);
                } catch (_) {
                    if (window.WGOverlay) {
                        window.WGOverlay.show(routeGenerationI18N.healthBusyNotice);
                    }
                }
            })
            // start generation
            .then(function () {
                const lt = getLocalTimeAndWeekday();
                return $.ajax({
                    url: START_API,
                    method: 'POST',
                    contentType: 'application/json',
                    dataType: 'json',
                    data: JSON.stringify({
                        user_query: userQ,
                        mode: mode,
                        mode_style: style,
                        origin_coords: origin,
                        destination_coords: null,
                        suggested_routes: [],
                        time: lt.time,
                        week_day: lt.week_day
                    })
                });
            })
            .done(function (res) {
                if (!res || res.ok === false) {
                    if (window.WGOverlay) window.WGOverlay.hide();
                    showError(res?.error || routeGenerationI18N.network);
                    return;
                }
                if (res.suggested_routes) {
                    if (window.WGOverlay) window.WGOverlay.hide();
                    renderUsage(res?.energy);
                    const lt = getLocalTimeAndWeekday();
                    initSuggestedRoutes(res.start_id, res.suggested_routes, {
                        START_API: START_API,
                        DECLINE_START_API: DECLINE_START_API,
                        routeGenerationI18N: window.WayGenAI?.routeGenerationI18N || {},
                        user_query: userQ,
                        mode: mode,
                        mode_style: style,
                        time: lt.time,
                        week_day: lt.week_day,
                        fastest: res.fastest || false,
                        onSuccess: function (finalRes) {
                            buildRouteFromStart(finalRes, {
                                mode: mode,
                                mode_style: style,
                                onSuccess: function (buildRes) {
                                    renderUsage(buildRes?.energy);
                                    showBuiltRoutesPopup(buildRes, {
                                        i18n: routeGenerationI18N
                                    });
                                },
                                onError: function (err) {
                                    showError(err.message);
                                }
                            });
                        }
                    });
                } else if (Array.isArray(res.routes)) {
                    buildRouteFromStart(res, {
                        mode: mode,
                        mode_style: style,
                        onSuccess: function (buildRes) {
                            renderUsage(buildRes?.energy);
                            showBuiltRoutesPopup(buildRes, {
                                i18n: routeGenerationI18N
                            });
                        },
                        onError: function (err) {
                            showError(err.message);
                        }
                    });
                } else {
                    if (window.WGOverlay) window.WGOverlay.hide();
                    showError(routeGenerationI18N.network);
                }
            })
            .fail(function (jqXHR) {
                if (window.WGOverlay) window.WGOverlay.hide();
                if (jqXHR && jqXHR.status === 401) {
                    handle401();
                    return;
                }
                const msg = jqXHR?.responseJSON?.error || routeGenerationI18N.network;
                showError(msg);
            })
            .always(function () {
                setLoading($locationInput, false);
                $countrySelect.prop('disabled', false);
                setLoading($locateBtn, false);
                setLoading($searchBtn, false);
                setBtnLoading($genBtn, false);
            });
        });
    }

    /**
     * Initializes suggested routes and manages the user interface for
     * waypoint suggestion, route creation, and map visualization.
     *
     * @param {number|string} start_id - The ID of the starting point, which can be a numeric or string value. This will be converted into a number if applicable.
     * @param {Object} suggested - Object containing information about the starting point, destination(s), and waypoints. Expected structure:
     *                             { origin: { lat: number, lon: number },
     *                               destination: { lat: number, lon: number }|Array<{ lat: number, lon: number }>,
     *                               waypoints: Array<{ name?: string, lat: number, lon: number, country?: string }> }
     *                             - `origin`: An object defining the latitude and longitude of the start point.
     *                             - `destination`: An object (or array if multiple destinations exist) defining the latitude and longitude of the destination point.
     *                             - `waypoints` (optional): An array of waypoint objects including name, latitude, longitude, and optional country information.
     * @param {Object} [ctx={}] - Contextual object containing optional overrides and additional configurations for functionality or internationalization (i18n).
     *                            - Includes properties such as `routeGenerationI18N` for custom text, `DECLINE_START_API`, and `START_API` for endpoint URLs.
     * @return {void} The method does not return a value, but rather initializes the GUI and backend interactions for route suggestions.
     */
    function initSuggestedRoutes(start_id, suggested, ctx = {}) {
        /**
         * Internationalization (i18n) text resources used for route generation and waypoint suggestions.
         * This object contains localized strings for UI elements, notifications, and descriptions
         * related to routing functionality. The text can be overridden or extended by external
         * resources through the `ctx.routeGenerationI18N` object.
         *
         * Properties:
         * - suggOrigin: Label for the origin/start point.
         * - suggDestination: Label for the destination point.
         * - suggWaypoint: Label for intermediate route waypoints.
         * - suggBtnAccept: Text for the button that confirms and generates the route.
         * - suggLegendOrigin: Description of the origin's color in the route legend.
         * - suggLegendDest: Description of the destination's color in the route legend.
         * - suggLegendWp: Description of the intermediate waypoint's color in the route legend.
         * - suggEmptyList: Message displayed when no waypoints are suggested.
         * - suggSending: Message displayed when submitting or processing a request.
         * - suggMax: Warning message when attempting to add more than the allowed number of waypoints.
         * - network: Error message displayed when a network issue occurs.
         */
        const I18N = Object.assign({
            suggOrigin: 'Start',
            suggDestination: 'Destination',
            suggWaypoint: 'Waypoint',
            suggBtnAccept: 'Accept & generate',
            suggLegendOrigin: 'Start (green)',
            suggLegendDest: 'Destination (blue)',
            suggLegendWp: 'Intermediate (accent)',
            suggEmptyList: 'No intermediate points suggested.',
            suggSending: 'Submitting…',
            suggMax: 'Maximum of 8 intermediate points allowed.',
            network: 'Network error. Please try again.'
        }, ctx.routeGenerationI18N || {});

        const MAX_ACTIVE = 8;
        const BRAND = '#3f7d82';

        const $dlg = $('#wg-suggest-overlay');
        const $map = $('#wg-sugg-map');
        const $list = $('#wg-sugg-list');
        const $accept = $('#wg-sugg-accept');
        const $cancel = $('#wg-sugg-cancel');
        const $regenerate = $('#wg-sugg-regenerate');
        const $note = $('#wg-sugg-note');

        const DECLINE_START_API = ctx.DECLINE_START_API || '/use_route/decline_start';
        const START_API = ctx.START_API || '/start';
        const startId = Number(start_id) || 0;

        let $warn = $('#wg-sugg-warn');
        if (!$warn.length) {
            $warn = $('<div id="wg-sugg-warn" class="wg-sugg-warn" style="display:none"></div>').insertAfter($note);
        }

        /**
         * Resets the state of buttons by enabling them, updating their classes, and adjusting their content as necessary.
         *
         * @return {void} This method does not return a value.
         */
        function resetButtons() {
            $accept.prop('disabled', false).removeClass('is-loading');
            $accept.find('.spinner').hide();
            $accept.text(I18N.suggBtnAccept);
            $cancel.prop('disabled', false);
            $regenerate.prop('disabled', false);
        }

        // show overlay
        $dlg.stop(true, true).fadeIn(120);
        $('html, body').addClass('no-scroll');
        resetButtons();
        $dlg.off('click.wglock').on('click.wglock', function (e) {
            const $inner = $(e.target).closest('.wg-suggest');
            if ($inner.length === 0) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        });
        $(document).off('keydown.wglock').on('keydown.wglock', function (e) {
            if (e.key === 'Escape' || e.keyCode === 27) {
                if ($dlg.is(':visible')) {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                }
            }
        });

        /**
         * Unlocks the interface and hides the modal or dialog element.
         * Removes any event listeners associated with the lock functionality,
         * fades out the dialog element, and re-enables scrolling on the page.
         *
         * @return {void} This method does not return a value.
         */
        function unlockAndHide() {
            $(document).off('keydown.wglock');
            $dlg.off('click.wglock');
            $dlg.fadeOut(120);
            $('html, body').removeClass('no-scroll');
        }

        // collect data
        const origin = suggested.origin;
        const destination = Array.isArray(suggested.destination) ? suggested.destination[0] : suggested.destination;
        let waypoints = (suggested.waypoints || []).map((wp, idx) => ({
            idx,
            name: wp.name || (I18N.suggWaypoint + ' ' + (idx + 1)),
            lat: Number(wp.lat),
            lon: Number(wp.lon),
            country: wp.country || origin.country,
            active: true
        }));

        // maps
        if ($map.data('leaflet-map')) {
            try {
                $map.data('leaflet-map').remove();
            } catch (_) {
            }
            $map.removeData('leaflet-map');
        }
        const map = L.map('wg-sugg-map', {zoomControl: true, scrollWheelZoom: true});
        $map.data('leaflet-map', map);
        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors &copy; <a href="https://www.carto.com/">CARTO</a>',
            subdomains: 'abcd',
            maxZoom: 20
        }).addTo(map);

        // auto-zoom
        let autoZoom = true;
        let fitTimer = null;

        /**
         * Constructs and returns a bounds object that includes the origin,
         * destination, and any active waypoints. The method calculates the
         * geographic bounds required to encompass all these coordinates.
         *
         * @return {Object} A LatLngBounds object representing the calculated geographic bounds.
         */
        function buildBounds() {
            const b = L.latLngBounds(
                L.latLng(origin.lat, origin.lon),
                L.latLng(destination.lat, destination.lon)
            );
            waypoints.filter(w => w.active).forEach(w => b.extend([w.lat, w.lon]));
            return b;
        }

        /**
         * Adjusts the map view to fit the current route displayed on the map.
         * If the `immediate` parameter is true, the adjustment is applied instantly.
         * Otherwise, it is applied after a short delay.
         *
         * @param {boolean} immediate - Determines if the map adjustment should happen immediately (true) or after a delay (false).
         * @return {void} Does not return a value.
         */
        function fitToRoute(immediate = false) {
            if (!autoZoom) return;
            const doFit = () => {
                const b = buildBounds();
                if (b.isValid()) map.fitBounds(b, {padding: [24, 24], maxZoom: 14, animate: true});
            };
            if (immediate) {
                if (fitTimer) {
                    clearTimeout(fitTimer);
                    fitTimer = null;
                }
                doFit();
            } else {
                if (fitTimer) clearTimeout(fitTimer);
                fitTimer = setTimeout(doFit, 140);
            }
        }

        // disable auto-zoom on moves
        map.on('zoomstart movestart dragstart', () => {
            autoZoom = false;
        });

        /**
         * Generates a custom map pin icon with the specified color.
         *
         * @param {string} color - The color used to fill the pin icon.
         * @return {Object} A Leaflet divIcon instance representing the custom pin icon.
         */
        function pin(color) {
            const svg = `
            <svg viewBox="0 0 24 24" width="26" height="26" style="display:block">
              <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7.02 11.54 7.32 11.8a1 1 0 0 0 1.35 0C12.98 21.54 20 15.25 20 10c0-4.42-3.58-8-8-8z" fill="${color}"/>
              <circle cx="12" cy="10" r="3.2" fill="#fff"/>
            </svg>`;
            return L.divIcon({className: 'wg-pin', html: svg, iconSize: [26, 26], iconAnchor: [13, 26]});
        }

        const pinOrigin = pin('#2f855a');
        const pinDest = pin('#2563eb');
        const pinWp = pin(BRAND);

        /**
         * Adds a marker to the map at a specified location with a given type and optional drag functionality.
         *
         * @param {Object} point - An object containing latitude and longitude coordinates of the marker.
         * @param {number} point.lat - The latitude of the location.
         * @param {number} point.lon - The longitude of the location.
         * @param {string} type - The type of marker to add ('origin', 'destination', or others).
         * @param {boolean} [draggable=false] - Specifies whether the marker is draggable.
         * @param {function} [onDragEnd=null] - Optional callback function to execute when dragging ends.
         *                                       The function is invoked with the new latitude, longitude, and the marker instance as arguments.
         * @return {Object} Returns the marker instance added to the map.
         */
        function addMarker(point, type, draggable = false, onDragEnd = null) {
            const icon = (type === 'origin') ? pinOrigin : (type === 'destination' ? pinDest : pinWp);
            const m = L.marker([point.lat, point.lon], {icon, draggable});
            m.addTo(map);
            if (onDragEnd) {
                m.on('dragend', function (ev) {
                    const ll = ev.target.getLatLng();
                    onDragEnd(ll.lat, ll.lng, m);
                });
            }
            return m;
        }

        addMarker(origin, 'origin', false, null);
        addMarker(destination, 'destination', false, null);

        // markers for waypoints
        waypoints.forEach(wp => {
            const m = addMarker(wp, 'waypoint', true, (lat, lon) => {
                wp.lat = Number(lat);
                wp.lon = Number(lon);
                $list.find(`[data-wp="${wp.idx}"]`).addClass('is-edited')
                    .find('.wg-sugg-row__coords').text(`${wp.lat.toFixed(5)}, ${wp.lon.toFixed(5)}`);
                redrawPolyline();
                fitToRoute();
            });
            wp._marker = m;
        });

        let line = null;

        /**
         * Redraws the polyline on the map by removing the existing line (if any) and creating a new one
         * based on the active waypoints, origin, and destination coordinates.
         *
         * @return {void} This function does not return a value.
         */
        function redrawPolyline() {
            if (line) {
                try {
                    map.removeLayer(line);
                } catch (_) {
                }
                line = null;
            }
            const pts = [origin]
                .concat(waypoints.filter(w => w.active).map(w => [w.lat, w.lon]))
                .concat([[destination.lat, destination.lon]]);
            line = L.polyline(pts, {weight: 4, opacity: 0.8, color: BRAND}).addTo(map);
        }

        redrawPolyline();
        fitToRoute(true);

        /**
         * Counts the number of active waypoints.
         *
         * @return {number} The number of waypoints where the `active` property is true.
         */
        function activeCount() {
            return waypoints.filter(w => w.active).length;
        }

        /**
         * Updates the visual and functional state of the accept button and related elements
         * based on the current*/
        function updateAcceptState() {
            const cnt = activeCount();
            $('.wg-sugg-cap').text(`(${cnt}/${MAX_ACTIVE})`);
            if (cnt > MAX_ACTIVE) {
                $accept.prop('disabled', true).addClass('is-disabled');
                $warn.text(I18N.suggMax).show();
            } else {
                $accept.prop('disabled', false).removeClass('is-disabled');
                $warn.hide();
            }
        }

        /**
         * Renders a list of waypoints, origin, and destination into a specified list element,
         * including interactive features such as sorting, toggling visibility of waypoints,
         * and managing active waypoint states.
         *
         * - Displays legends for origin, destination, and waypoint types.
         * - Handles the rendering of origin, waypoints, and destination.
         * - Allows reordering of waypoints via drag-and-drop functionality.
         * - Updates polyline and map display based on active waypoints status.
         * - Manages maximum active waypoints constraint.
         * - Adds no-results placeholder if no waypoints exist.
         *
         * @return {void} This function does not return a value. Its purpose is to modify the DOM of the provided $list element.
         */
        function renderList() {
            $list.empty();

            // legend
            $list.append(`
              <div class="wg-sugg-legend">
                <span class="chip chip--green"></span>${escHtml(I18N.suggLegendOrigin)}
                <span class="chip chip--blue"></span>${escHtml(I18N.suggLegendDest)}
                <span class="chip chip--accent"></span>${escHtml(I18N.suggLegendWp)}
                <span class="wg-sugg-cap"></span>
              </div>
            `);

            // origin
            $list.append(`
              <div class="wg-sugg-row is-fixed">
                <div class="wg-sugg-row__title">${escHtml(I18N.suggOrigin)}</div>
                <div class="wg-sugg-row__name">${escHtml(origin.name || '')}</div>
              </div>
            `);

            if (!waypoints.length) {
                $list.append(`<div class="wg-sugg-empty">${escHtml(I18N.suggEmptyList)}</div>`);
            }

            const $ul = $('<div id="wg-sugg-ul" class="wg-sugg-ul"/>');
            waypoints.forEach((wp) => {
                const $row = $(`
                    <div class="wg-sugg-row ${wp.active ? '' : 'is-inactive'}" data-wp="${wp.idx}">
                      <div class="wg-sugg-drag" title="Drag to reorder" aria-label="Drag to reorder" tabindex="0">⋮⋮</div>
                      <label class="wg-sugg-toggle">
                        <input type="checkbox" ${wp.active ? 'checked' : ''}>
                      </label>
                      <div class="wg-sugg-row__title">${escHtml(I18N.suggWaypoint)}</div>
                      <div class="wg-sugg-row__name">${escHtml(wp.name)}</div>
                      <div class="wg-sugg-row__coords">${wp.lat.toFixed(5)}, ${wp.lon.toFixed(5)}</div>
                    </div>
                  `);

                // activity toggle
                $row.find('input[type="checkbox"]').on('change', function () {
                    if (this.checked) {
                        const cntBefore = activeCount();
                        if (cntBefore >= MAX_ACTIVE) {
                            this.checked = false;
                            updateAcceptState();
                            return;
                        }
                        wp.active = true;
                        if (wp._marker) wp._marker.addTo(map);
                    } else {
                        wp.active = false;
                        if (wp._marker) {
                            try {
                                map.removeLayer(wp._marker);
                            } catch (_) {
                            }
                        }
                    }
                    redrawPolyline();
                    updateAcceptState();
                    fitToRoute();
                });

                $ul.append($row);
            });
            $list.append($ul);

            // destination
            $list.append(`
              <div class="wg-sugg-row is-fixed">
                <div class="wg-sugg-row__title">${escHtml(I18N.suggDestination)}</div>
                <div class="wg-sugg-row__name">${escHtml(destination.name || '')}</div>
              </div>
            `);

            // sorting
            $ul.sortable({
                handle: '.wg-sugg-drag',
                animation: 150,
                ghostClass: 'wg-sugg-ghost',
                onEnd: function () {
                    const orderIds = $ul.children('.wg-sugg-row').map(function () {
                        return Number(this.dataset.wp);
                    }).get();

                    waypoints.sort((a, b) => orderIds.indexOf(a.idx) - orderIds.indexOf(b.idx));
                    redrawPolyline();
                    updateAcceptState();
                    fitToRoute();
                }
            });

            updateAcceptState();
        }

        // init
        renderList();

        /**
         * Toggles the loading state of specific buttons and updates their properties accordingly.
         *
         * @param {boolean} on - Indicates whether to set the buttons into a loading state (true) or not (false).
         * @return {void} This function does not return a value.
         */
        function setBtnsLoading(on) {
            $accept.prop('disabled', !!on).toggleClass('is-loading', !!on);
            $cancel.prop('disabled', !!on);
            $regenerate.prop('disabled', !!on);
            $accept.find('.spinner').toggle(!!on);
            if (on) $accept.attr('data-label-orig', $accept.text()).text(I18N.suggSending);
            else $accept.text($accept.attr('data-label-orig') || I18N.suggBtnAccept);
        }

        /**
         * Sends a POST request to the DECLINE_START_API endpoint to decline a start action.
         * If the `startId` is not defined, a resolved promise is returned without making an API call.
         * The request includes the necessary headers and the `startId` in the body as JSON.
         *
         * @return {Promise} A promise that resolves with the result of the API call or immediately resolves if `startId` is undefined.
         */
        function declineStart() {
            if (!startId) return $.Deferred().resolve().promise();
            return $.ajax({
                url: DECLINE_START_API,
                method: 'POST',
                contentType: 'application/json',
                dataType: 'json',
                data: JSON.stringify({start_id: startId})
            });
        }

        // cancel
        $cancel.off('click').on('click', function () {
            $accept.prop('disabled', true);
            $cancel.prop('disabled', true);
            $regenerate.prop('disabled', true);
            declineStart()
                .always(function () {
                    unlockAndHide();
                    if (typeof ctx.onCancel === 'function') ctx.onCancel();
                });
        });

        // regenerate
        $regenerate.off('click').on('click', function () {
            $accept.prop('disabled', true);
            $cancel.prop('disabled', true);
            $regenerate.prop('disabled', true);
            declineStart()
                .always(function () {
                    unlockAndHide();
                    if (typeof ctx.onCancel === 'function') ctx.onCancel();
                    $('#gen-start').trigger('click');
                });
        });

        // accept
        $accept.off('click').on('click', function () {
            if (activeCount() > MAX_ACTIVE) {
                updateAcceptState();
                return;
            }

            setBtnsLoading(true);

            try {
                if (window.WGOverlay) window.WGOverlay.show(I18N.suggSending);
            } catch (_) {
            }

            const activeWps = waypoints.filter(w => w.active).slice(0, MAX_ACTIVE).map(w => ({
                name: w.name, lat: w.lat, lon: w.lon, country: w.country
            }));

            const payload = {
                user_query: ctx.user_query || '',
                mode: ctx.mode || 'driving',
                mode_style: ctx.mode_style || 'default',
                origin_coords: origin,
                destination_coords: null,
                suggested_routes: [{
                    origin: origin,
                    destination: destination,
                    waypoints: activeWps
                }],
                time: ctx.time,
                week_day: ctx.week_day,
                fastest: ctx.fastest || false
            };

            $.ajax({
                url: START_API,
                method: 'POST',
                contentType: 'application/json',
                dataType: 'json',
                data: JSON.stringify(payload)
            })
            .done(function (res) {
                unlockAndHide();
                if (typeof ctx.onSuccess === 'function') ctx.onSuccess(res);
            })
            .fail(function (jqXHR) {
                const msg = jqXHR?.responseJSON?.error || I18N.network;
                alert(msg);
            })
            .always(function () {
                setBtnsLoading(false);
            });
        });
    }

    /**
     * Builds a route from start using the provided start response and context.
     *
     * @param {Object} startRes - The response object containing information about the start and routes.
     *                            Must include `routes` (array of routes) and `start_id` (identifier for the start point).
     * @param {Object} [ctx={}] - Context object to customize behavior.
     *                            Properties:
     *                            - `mode`: The mode for building the route.
     *                            - `mode_style`: The style for the route mode.
     *                            - `onError`: A callback function invoked on error with an Error object as the argument.
     *                            - `onSuccess`: A callback function invoked on successful route building with the response as the argument.
     *
     * @return {void} This function does not return a value. All results are handled through callbacks provided in the context.
     */
    function buildRouteFromStart(startRes, ctx = {}) {
        /**
         * I18N is an object that stores internationalization (i18n) strings for the application.
         * It provides localized messages related to network errors and route generation.
         *
         * The `network` property contains a message displayed to the user when a network error occurs.
         * The `building` property contains a message displayed to the user while routes are being generated.
         *
         * Additionally, it merges any custom i18n strings provided via `window.WayGenAI.routeGenerationI18N`,
         * allowing the application to extend or override the default messages.
         */
        const I18N = Object.assign({
            network: 'Network error. Please try again.',
            building: 'Building route/-s…'
        }, window.WayGenAI?.routeGenerationI18N || {});
        const BUILD_API = window.WayGenAI?.apiBuildRouteBase || '/build_route';

        if (!startRes || !Array.isArray(startRes.routes) || !startRes.routes.length) {
            if (typeof ctx.onError === 'function') ctx.onError(new Error('No routes to build.'));
            return;
        }
        if (!ctx.mode || !ctx.mode_style) {
            if (typeof ctx.onError === 'function') ctx.onError(new Error('Missing mode or mode_style.'));
            return;
        }
        const startId = startRes.start_id;
        if (typeof startId === 'undefined' || startId === null) {
            if (typeof ctx.onError === 'function') ctx.onError(new Error('Missing start_id.'));
            return;
        }

        try {
            if (window.WGOverlay) window.WGOverlay.show(I18N.building);
        } catch (_) {
        }

        /**
         * Transforms and normalizes route data from the `startRes.routes` array into a new structure.
         *
         * The `routesPayload` variable processes an array of routes, ensuring that each route contains
         * a valid structure with origin, destination, and an array of route points. Missing or incomplete
         * data is supplemented, ensuring origin and destination points are correctly included in the route
         * points array.
         *
         * Key functionality:
         * - Ensures each route has `route_points` as an array.
         * - Verifies and adds the origin point to the start of the route points if it's missing.
         * - Verifies and adds the destination point to the end of the route points if it's missing.
         * - Returns a normalized structure for each route object containing origin, destination, and
         *   updated `route_points`.
         *
         * The resulting `routesPayload` is an array of the normalized route objects.
         */
        const routesPayload = startRes.routes.map((r) => {
            const src = (r && r.route && Array.isArray(r.route.route_points)) ? r.route : {
                origin: r.origin,
                destination: r.destination,
                route_points: Array.isArray(r.route_points) ? r.route_points : []
            };

            const pts = Array.isArray(src.route_points) ? src.route_points.slice() : [];
            const needOrigin = !pts.length || (pts[0].lat !== src.origin?.lat || pts[0].lon !== src.origin?.lon);
            const needDest = !pts.length || (pts[pts.length - 1].lat !== src.destination?.lat || pts[pts.length - 1].lon !== src.destination?.lon);

            if (needOrigin && src.origin) pts.unshift(src.origin);
            if (needDest && src.destination) pts.push(src.destination);

            return {
                origin: src.origin,
                destination: src.destination,
                route_points: pts
            };
        });

        const payload = {
            mode: ctx.mode,
            mode_style: ctx.mode_style,
            start_id: startId,
            routes: routesPayload
        };

        // build route
        $.ajax({
            url: BUILD_API,
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify(payload)
        })
        .done(function (res) {
            if (typeof ctx.onSuccess === 'function') ctx.onSuccess(res);
        })
        .fail(function (jqXHR) {
            const msg = jqXHR?.responseJSON?.detail || jqXHR?.responseJSON?.error || I18N.network;
            if (typeof ctx.onError === 'function') ctx.onError(new Error(msg));
        })
        .always(function () {
            try {
                if (window.WGOverlay) window.WGOverlay.hide();
            } catch (_) {
            }
        });
    }

    /**
     * Displays a popup for reviewing and managing built routes based on provided parameters.
     *
     * @param {Object} buildRes - The build results object containing route information such as `results`, `start_id`, `mode`, and `mode_style`.
     * @param {Object} [ctx={}] - An optional context object for additional configuration.
     *     - `ctx.i18n`: Localization strings for the popup.
     *     - `ctx.start_id`: A custom start ID overriding the one provided in `buildRes`.
     *     - `ctx.mode`: The mode of transportation.
     *     - `ctx.mode_style`: The style of the mode (e.g., default or custom styles).
     *
     * @return {void} This function does not return a value as it directly manipulates the DOM to display the popup and handles user interactions with the routes.
     */
    function showBuiltRoutesPopup(buildRes, ctx = {}) {
        /**
         * I18N is an internationalization object that provides user-facing strings for various application states.
         * It allows for dynamic localization by merging the default messages with context-specific translations.
         *
         * Properties:
         * - `start`: Message displayed when initiating a route.
         * - `noRoutes`: Message shown when no routes are available or found.
         * - `network`: Message shown when a network error occurs.
         *
         * The object is dynamically extended with additional translations from the `ctx.i18n` context,
         * if available, to support localization and custom messages.
         */
        const I18N = Object.assign({
            start: 'Start route',
            noRoutes: 'No routes available. Try again.',
            network: 'Network error. Please try again.'
        }, ctx.i18n || {});
        const ACCEPT_API = window.WayGenAI?.apiAcceptRouteBase || '/use_route/accept_route';
        const DECLINE_API = window.WayGenAI?.apiDeclineRoutesBase || '/use_route/decline_routes';

        const startId = ctx.start_id ?? buildRes.start_id;
        const mode = ctx.mode ?? buildRes.mode;
        const modeStyle = ctx.mode_style ?? buildRes.mode_style;

        const $ov = $('#wg-built-overlay');
        const $list = $('#wg-built-list');
        const $err = $('#wg-built-error');

        $err.hide().text('');
        $list.empty();

        $ov.find('[data-built-reject]').prop('disabled', false);

        /**
         * Handles HTTP 401 Unauthorized errors by performing necessary actions such as
         * clearing the authentication token and redirecting the user to the login page.
         * The function behavior is dependent on the availability of certain external
         * methods and properties.
         *
         * @return {void} Does not return a value.
         */
        function handle401() {
            if (typeof clearCachedUser === 'function') clearCachedUser();
            if (typeof hardRedirectToLogin === 'function') {
                hardRedirectToLogin();
            } else {
                window.location.assign(window.WayGenAI?.loginUrl || '/user/log-in/');
            }
        }

        /**
         * Generates a Google Maps link based on provided latitude, longitude,
         * and optionally a place ID. If a place ID is provided, it generates a
         * link to that specific place. Otherwise, it creates a query based
         * on the latitude and longitude.
         *
         * @param {number} lat - The latitude coordinate.
         * @param {number} lon - The longitude coordinate.
         * @param {string} [placeId] - Optional. The unique identifier for a specific place in Google Maps.
         * @return {string} A URL linking to the specified location or place in Google Maps.
         */
        function get_google_maps_link(lat, lon, placeId = '') {
            if (placeId) {
                return `https://www.google.com/maps/place/?q=place_id:${placeId}`;
            } else {
                const query = `${lat},${lon}`;
                const encoded = encodeURIComponent(query);
                return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
            }
        }

        /**
         * Processes an array of segment objects to generate a concatenated string
         * representation of links with provided location data.
         *
         * @param {Array} segments An array of segment objects, each containing information
         *                         about `from` and/or `to` locations, their coordinates (`from_coords` and `to_coords`),
         *                         and place IDs (`from_place_id` and `to_place_id`).
         * @return {string} A string containing HTML-formatted links to locations,
         *                  joined with directional indicators, or an empty string if no valid segments are provided.
         */
        function segmentsText(segments) {
            if (!Array.isArray(segments) || !segments.length) return '';

            const names = [];
            segments.forEach(s => {
                if (s?.from && (!names.length || names[names.length - 1].text !== s.from))
                    names.push({
                        text: s.from,
                        coords: s.from_coords,
                        place_id: s.from_place_id
                    });

                if (s?.to)
                    names.push({
                        text: s.to,
                        coords: s.to_coords,
                        place_id: s.to_place_id
                    });
            });

            return names.map((n, i) => {
                const href = n.coords
                    ? get_google_maps_link(
                        n.coords.lat,
                        n.coords.lon,
                        n.place_id || ''
                    )
                    : '#';

                const link = `<a href="${href}" target="_blank">${escHtml(n.text)}</a>`;
                const arrow = `<i class="fa fa-long-arrow-right" aria-hidden="true"></i>`;
                return i ? `${arrow} ${link}` : link;
            }).join(' ');
        }

        // check routes
        const results = Array.isArray(buildRes?.results) ? buildRes.results : [];
        if (!results.length) {
            $err.text(I18N.noRoutes).show();
        }

        // process and output routes
        results.forEach((item, idx) => {
            const rid = item.route_id;
            const links = item.map_links || {};
            const hasError = !!item.error;

            const gPreview = links.google_maps_preview || '';
            const segs = links.segments || [];

            const segHtml = segmentsText(segs);

            const $card = $(`
              <div class="wg-route-card" data-rid="${rid}">
                <div class="wg-route-card__media">
                  ${gPreview ? `<img src="${gPreview}" crossorigin="anonymous" alt="Route preview ${idx + 1}">` : ''}
                </div>
                <div class="wg-route-card__body">
                  <div class="wg-route-card__segments">
                    ${hasError ? `<span style="color:#b00020">${escHtml(item.error)}</span>` : (segHtml || '<em>No segments</em>')}
                  </div>
                  <div class="wg-route-card__actions">
                    <button class="wg-route-card__start btn-primary" ${hasError ? 'disabled data-has-error="1"' : ''}>
                      ${I18N.start}
                      <span class="spinner" style="display:none;margin-left:6px"></span>
                    </button>
                  </div>
                </div>
              </div>
            `);

            // use route
            $card.find('.wg-route-card__start').on('click', function () {
                if (hasError) return;
                const $btn = $(this);
                const $sp = $btn.find('.spinner');
                $ov.find('[data-built-reject]').prop('disabled', true);
                $ov.find('.wg-route-card__start').prop('disabled', true);
                $sp.show();

                if (!rid || !startId) {
                    $err.text(I18N.network).show();
                    $ov.find('[data-built-reject]').prop('disabled', false);
                    $ov.find('.wg-route-card__start:not([data-has-error="1"])').prop('disabled', false);
                    $sp.hide();
                    return;
                }

                const payload = {
                    start_id: Number(startId),
                    route_id: Number(rid),
                    mode: String(mode || 'driving'),
                    mode_style: String(modeStyle || 'default')
                };

                $.ajax({
                    url: ACCEPT_API,
                    method: 'POST',
                    contentType: 'application/json',
                    dataType: 'json',
                    data: JSON.stringify(payload)
                })
                .done(function (res) {
                    if (!res || res.ok === false || !res.hash) {
                        $err.text(res?.error || I18N.network).show();
                        $ov.find('[data-built-reject]').prop('disabled', false);
                        $ov.find('.wg-route-card__start:not([data-has-error="1"])').prop('disabled', false);
                        $sp.hide();
                        return;
                    }
                    // decline all others except current
                    const otherIds = results
                        .map(r => Number(r.route_id) || 0)
                        .filter(id => id && id !== Number(rid));

                    if (otherIds.length) {
                        $.ajax({
                            url: DECLINE_API,
                            method: 'POST',
                            contentType: 'application/json',
                            dataType: 'json',
                            data: JSON.stringify({route_ids: otherIds})
                        }).always(function () {
                            // redirect to route regardless of decline result
                            window.location.assign('/user/routes/' + encodeURIComponent(res.hash));
                        });
                    } else {
                        // if no others to decline, redirect immediately
                        window.location.assign('/user/routes/' + encodeURIComponent(res.hash));
                    }
                })
                .fail(function (jqXHR) {
                    if (jqXHR && jqXHR.status === 401 && typeof handle401 === 'function') {
                        handle401();
                        return;
                    }
                    const msg = jqXHR?.responseJSON?.detail || jqXHR?.responseJSON?.error || I18N.network;
                    $err.text(msg).show();
                    $ov.find('[data-built-reject]').prop('disabled', false);
                    $ov.find('.wg-route-card__start:not([data-has-error="1"])').prop('disabled', false);
                    $sp.hide();
                });
            });

            $list.append($card);
        });

        $ov.addClass('is-open').show().removeAttr('hidden');
        $('html, body').addClass('no-scroll');

        // decline all routes
        $ov.find('[data-built-reject]').off('click').on('click', function () {
            const $rejectBtn = $(this);
            const routeIds = results.map(r => Number(r.route_id) || 0).filter(Boolean);
            if (!routeIds.length) {
                $ov.removeClass('is-open').hide().attr('hidden', true);
                $('html, body').removeClass('no-scroll');
                return;
            }

            $rejectBtn.prop('disabled', true);
            $ov.find('.wg-route-card__start').prop('disabled', true);

            $.ajax({
                url: DECLINE_API,
                method: 'POST',
                contentType: 'application/json',
                dataType: 'json',
                data: JSON.stringify({route_ids: routeIds})
            })
            .done(function () {
                $ov.removeClass('is-open').hide().attr('hidden', true);
                $('html, body').removeClass('no-scroll');
            })
            .fail(function (jqXHR) {
                const msg = jqXHR?.responseJSON?.detail || jqXHR?.responseJSON?.error || I18N.network;
                $err.text(msg).show();
                $rejectBtn.prop('disabled', false);
                $ov.find('.wg-route-card__start:not([data-has-error="1"])').prop('disabled', false);
            });
        });

        // prevent popup from closing
        $ov.off('click.dismiss').on('click.dismiss', function (e) {
            if ($(e.target).is($ov)) {
                e.stopPropagation();
                e.preventDefault();
            }
        });
        $(document).off('keydown.wgBuilt').on('keydown.wgBuilt', function (e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
            }
        });
    }

    /**
     * Initializes and configures the route management interface. This includes setting up UI components,
     * event handlers, and data fetching mechanisms for managing routes (e.g., searching, filtering, displaying).
     *
     * The initialization involves:
     * - Checking for necessary DOM elements and halting execution if not present.
     * - Setting up internationalization (I18N) for UI text prompts.
     * - Configuring API endpoints for various actions like retrieving route history, declining routes, and finishing/accepting routes.
     * - Initializing state for pagination, search, and date filters.
     * - Configuring and initializing a date range picker for filtering results by date.
     *
     * The method also provides auxiliary functionality like formatting dates, managing button states,
     * and ensuring proper display of route thumbnails or placeholders.
     *
     * @return {void} This method does not return a value. It performs setup operations to enable the routing interface.
     */
    function initRoutes() {
        if (!$('.wg-my-routes').length) return;

        /**
         * I18N - Internationalization object containing various text strings used
         * for UI elements, error messages, and user prompts.
         *
         * Properties:
         * - noPreview: Text displayed when there is no preview available.
         * - searchPlaceholder: Placeholder text for the search input for routes.
         * - perPage: Label for items displayed per page.
         * - apply: Label for the apply button.
         * - notBuilt: Text indicating routes that have not been built.
         * - notFinished: Text indicating routes that are not finished.
         * - myRoutes: Label for "My Routes" section or feature.
         * - start: Text for the start button or action.
         * - continue: Text for the continue button or action.
         * - accept: Text for the accept button or confirmation.
         * - del: Text for the delete button or action.
         * - delConfirmRoute: Confirmation message prompt to delete a route.
         * - delConfirmStart: Confirmation message prompt to delete a start.
         * - network: Message displayed when there is a network error.
         * - invalidSession: Error message indicating expired or invalid user session.
         * - empty: Message displayed when no results or data are found.
         *
         * Additional translations or overrides can be applied by merging
         * the `window.WayGenAI.routesI18N` object if it exists.
         */
        const I18N = Object.assign({
            noPreview: 'No preview',
            searchPlaceholder: 'Search routes…',
            perPage: 'Per page',
            apply: 'Apply',
            notBuilt: 'Not Built Routes',
            notFinished: 'Not Finished Routes',
            myRoutes: 'My Routes',
            start: 'Start',
            continue: 'Continue',
            accept: 'Accept',
            del: 'Delete',
            delConfirmRoute: 'Delete this route?',
            delConfirmStart: 'Delete this start?',
            network: 'Network error. Please try again.',
            invalidSession: 'Your session has expired. Please log in again.',
            empty: 'Nothing found.',
        }, (window.WayGenAI?.routesI18N || {}));

        const ROUTE_HISTORY = window.WayGenAI?.apiRouteHistoryBase || '/route/history';
        const DECLINE_ROUTES = window.WayGenAI?.apiDeclineRoutesBase || '/use_route/decline_routes';
        const DECLINE_START = window.WayGenAI?.apiDeclineStartBase || '/use_route/decline_start';
        const FINISH_START = window.WayGenAI?.apiFinishStartBase || '/route/finish/start';
        const FINISH_ROUTE = window.WayGenAI?.apiFinishRouteBase || '/route/finish/route';
        const ACCEPT_ROUTE = window.WayGenAI?.apiAcceptRouteBase || '/use_route/accept_route';

        const $search = $('#wg-route-search');
        const $range = $('#wg-route-date-range');
        const $limit = $('#wg-route-limit');
        const $apply = $('#wg-route-apply');
        const $list = $('#wg-route-list');
        const $pager = $('#wg-route-pagination');

        const $nbList = $('#wg-not-built-list');
        const $nfList = $('#wg-not-finished-list');
        const $nbCnt = $('#wg-not-built-count');
        const $nfCnt = $('#wg-not-finished-count');

        const $nbWrap = $nbList.closest('details, .wg-collapsible').first();
        const $nfWrap = $nfList.closest('details, .wg-collapsible').first();

        let state = {
            page: 1,
            limit: Number($limit.val() || 20),
            q: '',
            date_from: '',
            date_to: ''
        };

        const fpBaseOpts = {
            dateFormat: 'Y-m-d',
            allowInput: true,
            disableMobile: true
        };
        let fpRange = null;

        /**
         * Formats a given Date object into a string in the format 'YYYY-MM-DD'.
         *
         * @param {Date} d - The Date object to be formatted.
         * @return {string} The formatted date string in 'YYYY-MM-DD' format.
         */
        function fmtYMD(d) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }

        /**
         * Converts the given input into a Date object, if possible.
         * Returns null if the conversion is invalid.
         *
         * @param {any} x - The input value to be converted to a Date object.
         * @return {Date|null} The corresponding Date object if valid, or null if the input cannot be converted to a valid date.
         */
        function toDate(x) {
            try {
                const d = new Date(x);
                return isNaN(+d) ? null : d;
            } catch (_) {
                return null;
            }
        }

        /**
         * Initializes a date range picker with specific options and binds it to an input field.
         * The datepicker restricts selection to a maximum of today's date and sets up a range selection mode.
         * Updates the state with the selected date range in the format `YYYY-MM-DD`.
         *
         * @return {void} This method does not return a value.
         */
        (function initDatepickerRange() {
            const today = new Date();
            fpRange = $range.flatpickr(Object.assign({}, fpBaseOpts, {
                mode: 'range',
                maxDate: today,
                onChange(selectedDates) {
                    const d1 = selectedDates[0] || null;
                    const d2 = selectedDates[1] || null;
                    state.date_from = d1 ? fmtYMD(d1) : '';
                    state.date_to = d2 ? fmtYMD(d2) : '';
                }
            }));
            $range.attr('placeholder', `${fmtYMD(today)} — ${fmtYMD(today)}`);
        })();

        /**
         * Updates the date bounds of a date range picker based on historical data provided.
         * Sets the minimum and maximum selectable dates, and adjusts the currently selected range if needed.
         *
         * @param {object} data - An object containing historical data. May include `min_date`, `max_date`, `results`, `items`, or raw data array, from which dates will be extracted and analyzed.
         * @return {void} This function does not return a value. It updates the date bounds and date range picker state in place.
         */
        function updateDateBoundsFromHistory(data) {
            let minAllowed = toDate(data?.min_date);
            let maxAllowed = toDate(data?.max_date);

            if (!minAllowed) {
                const items = Array.isArray(data?.results) ? data.results
                    : (Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []));
                for (const r of items) {
                    const d = toDate(r?.created_at || r?.date || r?.ts);
                    if (!d) continue;
                    if (!minAllowed || d < minAllowed) minAllowed = d;
                }
            }
            if (!maxAllowed) maxAllowed = new Date();

            if (fpRange) {
                fpRange.set('minDate', minAllowed || null);
                fpRange.set('maxDate', maxAllowed);

                if (!state.date_from && !state.date_to) {
                    const visFrom = minAllowed || maxAllowed;
                    fpRange.setDate([visFrom, maxAllowed], false);
                } else {
                    const curFrom = toDate(state.date_from) || null;
                    const curTo = toDate(state.date_to) || null;
                    let clampedFrom = curFrom, clampedTo = curTo;

                    if (curFrom && minAllowed && curFrom < minAllowed) clampedFrom = minAllowed;
                    if (curFrom && maxAllowed && curFrom > maxAllowed) clampedFrom = maxAllowed;
                    if (curTo && minAllowed && curTo < minAllowed) clampedTo = minAllowed;
                    if (curTo && maxAllowed && curTo > maxAllowed) clampedTo = maxAllowed;
                    if (clampedFrom && clampedTo && clampedFrom > clampedTo) clampedFrom = clampedTo;

                    if (clampedFrom || clampedTo) {
                        const arr = [clampedFrom || clampedTo, clampedTo || clampedFrom].filter(Boolean);
                        fpRange.setDate(arr, false);
                        state.date_from = arr[0] ? fmtYMD(arr[0]) : '';
                        state.date_to = arr[1] ? fmtYMD(arr[1]) : '';
                    }
                }
            }
        }

        /**
         * Toggles the loading state of a button by enabling/disabling it and showing/hiding a spinner element.
         *
         * @param {jQuery} $btn - The jQuery object representing the button to be modified.
         * @param {boolean} on - A boolean value; if true, the button is disabled and the spinner is displayed,
         *                       if false, the button is enabled and the spinner is hidden.
         * @return {void}
         */
        function setBtnLoading($btn, on) {
            $btn.prop('disabled', !!on);
            $btn.find('.spinner').toggle(!!on);
        }

        /**
         * Formats a given timestamp or ISO string into a locale date string.
         *
         * @param {number|string} tsOrIso - A timestamp (number) or an ISO date string to format.
         * @return {string} A string representing the formatted date in the locale format, or an empty string if invalid input is provided.
         */
        function fmtDate(tsOrIso) {
            try {
                const d = new Date(tsOrIso);
                if (isNaN(+d)) return '';
                return d.toLocaleDateString();
            } catch {
                return '';
            }
        }

        /**
         * Ensures the provided value is an array. If the input is already an array, it is returned as-is.
         * If the input is not an array, an empty array is returned.
         *
         * @param {*} a - The value to be checked and possibly converted to an array.
         * @return {Array} The input value if it is an array, otherwise an empty array.
         */
        function ensureArray(a) {
            return Array.isArray(a) ? a : [];
        }

        /**
         * Generates a thumbnail image element or a placeholder when a URL is not provided.
         *
         * @param {string} url - The URL of the image to be used as the thumbnail. If this is falsy, a placeholder will be returned.
         * @return {string} A string containing either an HTML `<img>` element with the specified URL or a placeholder `<div>` element.
         */
        function thumbOrPlaceholder(url) {
            if (!url) return '<div class="wg-thumb-placeholder">' + I18N.noPreview + '</div>';
            const cors = url.startsWith('https://maps.googleapis.com/maps/api/staticmap?')
                ? ' crossorigin="anonymous"' : '';
            return `<img src="${url}"${cors} alt="">`;
        }

        /**
         * Handles a 401 Unauthorized error by performing necessary actions such as clearing tokens
         * and redirecting the user to the login page.
         *
         * @return {void} Does not return a value.
         */
        function handle401() {
            if (typeof clearCachedUser === 'function') clearCachedUser();
            if (typeof hardRedirectToLogin === 'function') {
                hardRedirectToLogin();
            } else {
                window.location.assign(window.WayGenAI?.loginUrl || '/user/log-in/');
            }
        }

        /**
         * Updates the counters and visibility toggles based on the number of child elements
         * with the 'wg-route-card' class within the specified lists.
         *
         * The method recalculates the count of items in `$nbList` and `$nfList`, updates
         * the respective counter elements `$nbCnt` and `$nfCnt`, and toggles the visibility
         * of `$nbWrap` and `$nfWrap` based on whether the counts are greater than zero.
         *
         * @return {void} This function does not return a value.
         */
        function recalcCountersAndToggles() {
            const nbCount = $nbList.children('.wg-route-card').length;
            const nfCount = $nfList.children('.wg-route-card').length;
            $nbCnt.text(nbCount);
            $nfCnt.text(nfCount);
            if ($nbWrap.length) $nbWrap.toggle(nbCount > 0);
            if ($nfWrap.length) $nfWrap.toggle(nfCount > 0);
        }

        /**
         * Fetches the history data by creating a URL query string based on state parameters.
         *
         * @param {boolean} [suppressDates=false] - Indicates whether to exclude date filters from the request.
         * @return {jqXHR} The jQuery Promise object representing the asynchronous HTTP GET request.
         */
        function fetchHistory(suppressDates = false) {
            const params = new URLSearchParams();
            const offset = Math.max(0, (Number(state.page) - 1) * Number(state.limit));

            params.set('offset', String(offset));
            params.set('limit', String(state.limit));
            if (state.q) params.set('q', state.q);

            if (!suppressDates) {
                if (state.date_from) params.set('date_from', state.date_from);
                if (state.date_to) params.set('date_to', state.date_to);
            }

            // set separate loader
            $('body').addClass('wg-routes-history-not-loaded');

            return $.ajax({
                url: `${ROUTE_HISTORY}?${params.toString()}`,
                method: 'GET',
                dataType: 'json'
            }).fail(jq => {
                if (jq.status === 401 && typeof handle401 === 'function') return handle401();
                alert(jq?.responseJSON?.error || I18N.network);
            })
            .always(function () {
                $('body').removeClass('wg-routes-history-not-loaded');
            });
        }

        /**
         * Sends an AJAX GET request to the specified FINISH_START URL with the appropriate authentication headers.
         * Handles potential 401 unauthorized errors by invoking the `handle401` function if it is defined.
         * Displays an alert with either the error message from the server's response or a network error message if applicable.
         *
         * @return {Promise} A promise that resolves with the server's response data in JSON format or rejects with an error.
         */
        function fetchNotBuilt() {
            return $.ajax({
                url: FINISH_START,
                method: 'GET',
                dataType: 'json'
            }).fail(jq => {
                if (jq.status === 401 && typeof handle401 === 'function') return handle401();
                alert(jq?.responseJSON?.error || I18N.network);
            });
        }

        /**
         * Performs an AJAX request to fetch items that are not finished.
         *
         * The function sends a GET request to the predefined `FINISH_ROUTE` URL
         * with necessary authentication headers. In case the request fails with
         * a 401 status code, it tries to handle it using the `handle401` function
         * if it is defined. For other errors, an alert is displayed showing the
         * error message or a network-related default.
         *
         * @return {jqXHR} A jQuery XML HTTP Request (jqXHR) object
         * representing the asynchronous operation, which resolves with the
         * server's response if successful, or rejects with an error object if it fails.
         */
        function fetchNotFinished() {
            return $.ajax({
                url: FINISH_ROUTE,
                method: 'GET',
                dataType: 'json'
            }).fail(jq => {
                if (jq.status === 401 && typeof handle401 === 'function') return handle401();
                alert(jq?.responseJSON?.error || I18N.network);
            });
        }

        /**
         * Renders the history of routes onto the user interface.
         *
         * @param {Object} data - The data object containing route information to be rendered.
         * @param {Array} [data.results] - An array of route results to display.
         * @param {Array} [data.items] - An alternative array of route items to display.
         * @param {Object} [data.pages] - Metadata about page count and the current page.
         * @param {number} [data.pages.page] - The current page number from the dataset.
         * @param {number} [data.pages.pages] - The total number of pages available.
         * @param {number} [data.pages.count_on_page] - The number of items on the current page.
         * @param {number} [data.total] - The total number of routes if available.
         *
         * @return {void} This function does not return a value. It updates the user interface by modifying the document structure to display the route data and pagination.
         */
        function renderHistory(data) {
            const items = ensureArray(data?.results || data?.items || []);
            const meta = data?.pages || null;

            let page = Number(state.page);
            let pages = null;
            let per = Number(state.limit);
            let countOnPage = items.length;

            if (meta && Number.isFinite(meta.page) && Number.isFinite(meta.pages)) {
                page = Number(meta.page);
                pages = Number(meta.pages);
                countOnPage = Number(meta.count_on_page ?? items.length);
                state.page = page;
            } else {
                const total = Number(data?.total ?? NaN);
                const hasTotal = !isNaN(total);
                pages = hasTotal ? Math.max(1, Math.ceil(total / per)) : null;
            }

            $list.empty();
            if (!items.length) {
                $list.append(`<div class="wg-note">${escHtml(I18N.empty)}</div>`);
            }

            items.forEach((r) => {
                const historyId = r.id ?? r.history_id ?? r.route_history_id;
                const routeId = r.route_id ?? r.route?.id ?? r.id;

                const hash = r.hash || '';
                const segs = ensureArray(r.segments);
                const name1 = segs[0]?.from || r.origin?.name || '—';
                const name2 = (segs[segs.length - 1]?.to) || r.destination?.name || '—';
                const date = fmtDate(r.created_at || r.date || r.ts);
                const preview = r.google_maps_preview_base64 || r.thumbnail || '';

                const via = segs.map(s => s.from).slice(1);
                const chipsHtml = via.length ? `<div class="chips">${via.slice(0, 6).map(n => `<span class="chip">${escHtml(n)}</span>`).join('')}</div>` : '';

                const $card = $(`
                    <div class="wg-route-card" data-route="${historyId}">
                      <div class="wg-route-card__media">${thumbOrPlaceholder(preview)}</div>
                      <div class="wg-route-card__body">
                        <div class="wg-route-card__header">
                          <div>${escHtml(name1)} &nbsp;→&nbsp; ${escHtml(name2)}</div>
                          <div class="wg-route-card__date">${escHtml(date)}</div>
                        </div>
                        <div class="wg-route-card__meta">${chipsHtml}</div>
                        <div class="wg-route-card__actions">
                          <button class="btn btn-danger"  data-delete-route="${routeId}">${escHtml(I18N.del)}</button>
                          <button class="btn btn-primary" data-open-route="${historyId}" data-hash="${hash}">${escHtml(I18N.start)}</button>
                        </div>
                      </div>
                    </div>
                `);
                $list.append($card);
            });

            renderPagination(page, pages, countOnPage, per);
            bindHistoryActions();
        }

        /**
         * Renders a pagination component based on the current page, total pages, and items per page.
         *
         * @param {number} cur - The current page number.
         * @param {number} pages - The total number of pages.
         * @param {number} countOnPage - The number of items currently displayed on the page.
         * @param {number} perPage - The number of items to display per page.
         * @return {void} This method does not return a value.
         */
        function renderPagination(cur, pages, countOnPage, perPage) {
            $pager.empty();

            if (!pages || pages <= 1) {
                $pager.hide();
                return;
            }
            $pager.show();

            /**
             * Adds a pager button to the pager container with specific functionality and state.
             *
             * @param {string} label - The label to display on the button.
             * @param {number} page - The page number associated with the button.
             * @param {boolean} disabled - Determines if the button is disabled.
             * @param {boolean} active - Determines if the button is active.
             * @return {void} This function does not return a value.
             */
            function add(label, page, disabled, active) {
                const $b = $(`<button class="pager">${escHtml(label)}</button>`);
                if (disabled) $b.addClass('is-disabled');
                if (active) $b.addClass('is-active');
                if (!disabled && !active) {
                    $b.on('click', () => {
                        state.page = page;
                        loadHistory();
                    });
                }
                $pager.append($b);
            }

            add('«', 1, cur === 1, false);
            add('‹', Math.max(1, cur - 1), cur === 1, false);

            const windowSize = 4;
            const start = Math.max(1, cur - Math.floor(windowSize / 2));
            const end = Math.min(pages, start + windowSize - 1);
            for (let p = start; p <= end; p++) add(String(p), p, false, p === cur);

            add('›', Math.min(pages, cur + 1), cur === pages, false);
            add('»', pages, cur === pages, false);
        }

        /**
         * Renders a list of "not built" items based on the provided data. It populates the UI with relevant cards and binds actions to them.
         *
         * @param {Object} data - The data used to populate the list. It may contain an array of results, items, or other properties to derive the items from.
         * @return {void} This function does not return a value. It directly manipulates the DOM and binds actions to the rendered cards.
         */
        function renderNotBuilt(data) {
            const items = ensureArray(data?.results || data?.items || data || []);
            $nbList.empty();

            items.forEach(s => {
                const sid = s.start_id ?? s.id ?? s.start?.id;
                const date = fmtDate(s.created_at || s.date || s.ts);

                const name1 = s.data?.routes?.[0]?.origin?.name
                    || s.data?.suggested_routes?.origin?.name
                    || s.origin?.name || s.route?.origin?.name || '—';
                const q = s.data?.user_query || s.user_query || s.query || '';

                const $card = $(`
                    <div class="wg-route-card" data-start="${sid}">
                      <div class="wg-route-card__media"><div class="wg-thumb-placeholder">${escHtml(I18N.noPreview)}</div></div>
                      <div class="wg-route-card__body">
                        <div class="wg-route-card__header">
                          <div>${escHtml(name1)}</div>
                          <div class="wg-route-card__date">${escHtml(date)}</div>
                        </div>
                        <div class="wg-route-card__meta">${escHtml(q)}</div>
                        <div class="wg-route-card__actions">
                          <button class="btn btn-danger"  data-delete-start="${sid}">${escHtml(I18N.del)}</button>
                          <button class="btn btn-primary" data-continue-start="${sid}">${escHtml(I18N.continue)}</button>
                        </div>
                      </div>
                    </div>
                `);

                $card.data('start-payload', s.data || {});
                $card.data('mode', s.data?.mode || window.WayGenAI?.defaultMode || 'driving');
                $card.data('mode_style', s.data?.mode_style || window.WayGenAI?.defaultModeStyle || 'default');

                $nbList.append($card);
            });

            $nbCnt.text(items.length);
            if ($nbWrap.length) $nbWrap.toggle(items.length > 0);

            bindNotBuiltActions();
        }

        /**
         * Renders a list of not finished routes in the interface and binds necessary actions to them.
         *
         * @param {Object} data The input data containing route information. It expects an object with optional
         *                      `results`, `items`, or other route-related properties, such as `routes`.
         * @return {void} This function does not return a value. It updates the DOM with the generated route cards.
         */
        function renderNotFinished(data) {
            const items = ensureArray(data?.results || data?.items || data || []);
            $nfList.empty();

            items.forEach(r => {
                const rid = r.route_id ?? r.id;
                const date = fmtDate(r.created_at || r.date || r.ts);
                const segs = ensureArray(r.segments);
                const firstSeg = segs[0] || null;
                const lastSeg = segs.length ? segs[segs.length - 1] : null;

                const name1 = r.origin?.name || firstSeg?.from || '—';
                const name2 = r.destination?.name || lastSeg?.to || '—';

                const preview = r.google_maps_preview
                    || (r.map_links && r.map_links.google_maps_preview)
                    || r.thumbnail || '';

                const links = Object.assign(
                    {},
                    (r.map_links || {}),
                    {
                        google_maps: (r.map_links && r.map_links.google_maps) || r.google_maps || '',
                        apple_maps: (r.map_links && r.map_links.apple_maps) || r.apple_maps || '',
                        waze: (r.map_links && r.map_links.waze) || r.waze || ''
                    }
                );
                if (!links.google_maps_preview && r.google_maps_preview) {
                    links.google_maps_preview = r.google_maps_preview;
                }

                const $card = $(`
                    <div class="wg-route-card" data-nf-route="${rid}">
                      <div class="wg-route-card__media">${thumbOrPlaceholder(preview)}</div>
                      <div class="wg-route-card__body">
                        <div class="wg-route-card__header">
                          <div>${escHtml(name1)} &nbsp;→&nbsp; ${escHtml(name2)}</div>
                          <div class="wg-route-card__date">${escHtml(date)}</div>
                        </div>
                        <div class="wg-route-card__actions">
                          <button class="btn btn-danger"  data-reject-route="${rid}">${escHtml(I18N.del)}</button>
                          <button class="btn btn-primary" data-accept-route="${rid}">${escHtml(I18N.accept)}</button>
                        </div>
                      </div>
                    </div>
                `);

                $card.data('route-payload', {
                    route_id: rid,
                    start_id: r.start_id || 0
                });

                $nfList.append($card);
            });

            $nfCnt.text(items.length);
            if ($nfWrap.length) $nfWrap.toggle(items.length > 0);

            bindNotFinishedActions();
        }

        /**
         * Attaches event listeners to elements for handling actions related to route opening and deletion.
         *
         * The method binds click events to elements with specific data attributes:
         * - `[data-open-route]`: Redirects the user to a specific route page based on the provided `data-hash` attribute.
         * - `[data-delete-route]`: Sends an AJAX request to delete a route identified by the provided `data-delete-route` attribute and removes the corresponding element from the DOM on success.
         *
         * @return {void} This function does not return a value.
         */
        function bindHistoryActions() {
            $(document).off('click.myRoutesOpen').on('click.myRoutesOpen', '[data-open-route]', function () {
                const hash = this.getAttribute('data-hash') || '';
                if (!hash) {
                    alert(I18N.network);
                    return;
                }
                window.location.assign('/user/routes/' + encodeURIComponent(hash));
            });

            $(document).off('click.myRoutesDelete').on('click.myRoutesDelete', '[data-delete-route]', function () {
                const rid = Number(this.getAttribute('data-delete-route'));
                if (!rid) return;
                if (!confirm(I18N.delConfirmRoute)) return;

                // set separate loader
                $('body').addClass('wg-request-in-progress');

                $.ajax({
                    url: DECLINE_ROUTES,
                    method: 'POST',
                    contentType: 'application/json',
                    dataType: 'json',
                    data: JSON.stringify({route_ids: [rid]})
                }).done(() => {
                    $(this).closest('.wg-route-card').remove();
                }).fail(jq => {
                    if (jq.status === 401 && typeof handle401 === 'function') return handle401();
                    alert(jq?.responseJSON?.detail || jq?.responseJSON?.error || I18N.network);
                }).always(() => {
                    $('body').removeClass('wg-request-in-progress');
                });
            });
        }

        /**
         * Attaches event handlers to elements for specific actions related to deleting and continuing tasks.
         *
         * The method adds two event listeners:
         * - A handler for click events on elements with the `data-delete-start` attribute to handle delete actions.
         * - A handler for click events on elements with the `data-continue-start` attribute to handle continue actions.
         *
         * The delete operation prompts for user confirmation, sends an AJAX POST request to remove the specified data,
         * and updates the UI accordingly upon success.
         *
         * The continue operation verifies that required data is present, builds a route using the associated payload,
         * and handles success or failure responses, updating the UI and showing appropriate messages.
         *
         * @return {void} Does not return a value, as this function binds event handlers that manage application state and UI updates.
         */
        function bindNotBuiltActions() {
            $(document).off('click.nbDelete').on('click.nbDelete', '[data-delete-start]', function () {
                const sid = Number(this.getAttribute('data-delete-start'));
                if (!sid) return;
                if (!confirm(I18N.delConfirmStart)) return;

                // set separate loader
                $('body').addClass('wg-request-in-progress');

                $.ajax({
                    url: DECLINE_START,
                    method: 'POST',
                    contentType: 'application/json',
                    dataType: 'json',
                    data: JSON.stringify({start_id: sid})
                }).done(() => {
                    $(this).closest('.wg-route-card').remove();
                    recalcCountersAndToggles();
                }).fail(jq => {
                    if (jq.status === 401 && typeof handle401 === 'function') return handle401();
                    alert(jq?.responseJSON?.detail || jq?.responseJSON?.error || I18N.network);
                }).always(() => {
                    $('body').removeClass('wg-request-in-progress');
                });
            });

            // finish build route
            $(document).off('click.nbContinue').on('click.nbContinue', '[data-continue-start]', function () {
                const $card = $(this).closest('.wg-route-card');
                const sid = Number(this.getAttribute('data-continue-start'));
                const payload = $card.data('start-payload') || {};
                const mode = $card.data('mode') || 'driving';
                const style = $card.data('mode_style') || 'default';

                if (!sid) {
                    alert(I18N.network);
                    return;
                }
                if (!Array.isArray(payload.routes) || !payload.routes.length) {
                    alert('No routes to build for this start.');
                    return;
                }

                buildRouteFromStart(
                    Object.assign({start_id: sid}, payload),
                    {
                        mode,
                        mode_style: style,
                        onSuccess: function (buildRes) {
                            renderUsage(buildRes?.energy);
                            showBuiltRoutesPopup(buildRes, {
                                i18n: window.WayGenAI?.routeGenerationI18N || {}
                            });
                        },
                        onError: function (err) {
                            alert(err.message || I18N.network);
                        }
                    }
                );
            });
        }

        /**
         * Binds click event handlers to elements with `data-reject-route` and `data-accept-route` attributes.
         * Handles the logic for rejecting and accepting routes by sending AJAX requests to relevant endpoints
         * and performing DOM updates based on the responses. Updates counters, toggles, and navigates to the
         * appropriate route page after successful operations.
         *
         * @return {void} This function does not return a value.
         */
        function bindNotFinishedActions() {
            $(document).off('click.nfReject').on('click.nfReject', '[data-reject-route]', function () {
                const rid = Number(this.getAttribute('data-reject-route'));
                if (!rid) return;

                // set separate loader
                $('body').addClass('wg-request-in-progress');

                $.ajax({
                    url: DECLINE_ROUTES,
                    method: 'POST',
                    contentType: 'application/json',
                    dataType: 'json',
                    data: JSON.stringify({route_ids: [rid]})
                }).done(() => {
                    $(this).closest('.wg-route-card').remove();
                    recalcCountersAndToggles();
                }).fail(jq => {
                    if (jq.status === 401 && typeof handle401 === 'function') return handle401();
                    alert(jq?.responseJSON?.detail || jq?.responseJSON?.error || I18N.network);
                }).always(() => {
                    $('body').removeClass('wg-request-in-progress');
                });
            });

            // accept route
            $(document).off('click.nfAccept').on('click.nfAccept', '[data-accept-route]', function () {
                const rid = Number(this.getAttribute('data-accept-route'));
                if (!rid) return;

                const $card = $(this).closest('.wg-route-card');
                const data = $card.data('route-payload') || {};
                if (!data.start_id) return;

                const payload = {
                    start_id: Number(data.start_id),
                    route_id: rid,
                    mode: 'driving',
                    mode_style: 'default'
                };

                // set separate loader
                $('body').addClass('wg-request-in-progress');

                $.ajax({
                    url: ACCEPT_ROUTE,
                    method: 'POST',
                    contentType: 'application/json',
                    dataType: 'json',
                    data: JSON.stringify(payload)
                }).done((res) => {
                    if (!res || res.ok === false || !res.hash) {
                        $('body').removeClass('wg-request-in-progress');
                        alert(res?.error || I18N.network);
                        return;
                    }

                    const sid = payload.start_id;
                    const otherIds = [];
                    if (sid) {
                        $('[data-nf-route]').each(function () {
                            const other = $(this).data('route-payload') || {};
                            if (Number(other.start_id) === sid && Number(other.route_id) !== rid) {
                                otherIds.push(Number(other.route_id));
                            }
                        });
                    }

                    /**
                     * Handles post-cleanup operations and navigates the user to a specified route.
                     *
                     * This function performs cleanup tasks depending on the presence of a `sid` variable.
                     * If `sid` is present, it searches for elements with a specific data attribute,
                     * checks their associated `start_id`, and removes matching elements.
                     * If `sid` is not present, it removes a card element.
                     * After performing the cleanup, it recalculates associated counters and toggles,
                     * then redirects the user to a new route based on the hash from a response object.
                     *
                     * @param {number} sid - The start ID to match for cleanup operations.
                     * @param {object} res - The response object containing the route hash for redirection.
                     * @param {jQuery} $card - The jQuery element to be removed if no `sid` is present.
                     * @function
                     */
                    const afterCleanupAndGo = () => {
                        if (sid) {
                            $('[data-nf-route]').each(function () {
                                const other = $(this).data('route-payload') || {};
                                if (Number(other.start_id) === sid) $(this).remove();
                            });
                            recalcCountersAndToggles();
                        } else {
                            $card.remove();
                            recalcCountersAndToggles();
                        }
                        $('body').removeClass('wg-request-in-progress');
                        window.location.assign('/user/routes/' + encodeURIComponent(res.hash));
                    };

                    if (otherIds.length) {
                        $.ajax({
                            url: DECLINE_ROUTES,
                            method: 'POST',
                            contentType: 'application/json',
                            dataType: 'json',
                            data: JSON.stringify({route_ids: otherIds})
                        }).always(afterCleanupAndGo);
                    } else {
                        afterCleanupAndGo();
                    }
                }).fail(jq => {
                    $('body').removeClass('wg-request-in-progress');
                    if (jq.status === 401 && typeof handle401 === 'function') return handle401();
                    alert(jq?.responseJSON?.detail || jq?.responseJSON?.error || I18N.network);
                });
            });
        }

        /**
         * Loads and processes all required data sets by fetching "not built" and "not finished" items,
         * then renders the corresponding views and recalculates related counters and toggles.
         * Additionally, loads historical data.
         *
         * @return {void} This function does not return a value.
         */
        function loadAll() {
            $.when(fetchNotBuilt(), fetchNotFinished()).done((nb, nf) => {
                const nbData = Array.isArray(nb) ? nb[0] : nb;
                const nfData = Array.isArray(nf) ? nf[0] : nf;
                renderNotBuilt(nbData);
                renderNotFinished(nfData);
                recalcCountersAndToggles();
            });
            loadHistory(true);
        }

        /**
         * Loads and renders historical data, including energy usage and date bounds.
         *
         * @param {boolean} [suppressDates=false] Specifies whether to suppress date information during the fetch operation.
         * @return {void} Does not return a value; updates the UI with fetched historical data and usage information.
         */
        function loadHistory(suppressDates = false) {
            setBtnLoading($apply, true);
            fetchHistory(suppressDates).done((data) => {
                renderHistory(data);
                renderUsage(data?.energy);
                updateDateBoundsFromHistory(data);
            }).always(() => setBtnLoading($apply, false));
        }

        $limit.on('change', function () {
            state.limit = Number(this.value || 20);
            state.page = 1;
        });

        $apply.on('click', function () {
            state.q = ($search.val() || '').trim();

            if (fpRange) {
                const dates = fpRange.selectedDates || [];
                state.date_from = dates[0] ? fmtYMD(dates[0]) : '';
                state.date_to = dates[1] ? fmtYMD(dates[1]) : '';
            } else {
                const raw = ($range.val() || '').trim();
                const parts = raw.split(/\s*to\s*|\s*[–—\-]\s*/i);
                state.date_from = (parts[0] || '').trim();
                state.date_to = (parts[1] || '').trim();
            }

            state.page = 1;
            loadHistory(false);
        });

        $search.on('keydown', function (e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                $apply.trigger('click');
            }
        });

        // init
        loadAll();
    }

    /**
     * Initializes the plan usage interface, dynamically updating the user's current plan,
     * available actions (e.g., upgrade/downgrade), and subscription details within the UI.
     * It also maintains integration with the billing portal and handles interactions such as
     * managing subscriptions or fetching and displaying localized strings and settings.
     *
     * @return {void} This function does not return a value.
     */
    function initPlanUsage() {
        if (!$('.wg-plan-usage').length) return;

        /**
         * Internationalization (I18N) object containing localized strings and messages for plan and billing management.
         * This object merges predefined translations with external application-specific translations, if available.
         *
         * Properties:
         * - `current` (string): Label for the current plan.
         * - `upgrade` (string): Label indicating the upgrade option.
         * - `downgrade` (string): Label indicating the downgrade option.
         * - `manage` (string): Label for managing plans.
         * - `periodUntil` (string): Message indicating the current period and when credits or limits will be updated.
         * - `free` (string): Label for the free plan.
         * - `path` (string): Identifier or label for the "Pathfinder" plan.
         * - `trail` (string): Identifier or label for the "Trailblazer" plan.
         * - `unableOpenBilling` (string): Error message when the billing portal cannot be opened.
         * - `noUpgradePlan` (string): Message indicating that no upgrade plan has been configured.
         * - `unableCheckoutSession` (string): Error message when a checkout session cannot be created.
         * - `links` (object): External links related to plan usage or billing, sourced dynamically.
         */
        const I18N = Object.assign({
            current: 'Current plan',
            upgrade: 'Upgrade',
            downgrade: 'Downgrade',
            manage: 'Manage plan',
            periodUntil: 'This period, credits and limits are valid until <strong>%s</strong>. They will be updated and recalculated automatically.',
            free: 'Free',
            path: 'Pathfinder',
            trail: 'Trailblazer',
            unableOpenBilling: 'Unable to open billing portal right now.',
            noUpgradePlan: 'No upgrade plan configured.',
            unableCheckoutSession: 'Unable to create checkout session.',
            links: (window.WayGenAI?.planUsageLinks || {}),
        }, (window.WayGenAI?.planUsageI18n || {}));

        const API_BASE = window.WayGenAI?.apiUserStripeBase || '/stripe';

        const user = (typeof getUser === 'function') ? getUser() : null;
        if (!user) return;

        /**
         * Handles the HTTP 401 Unauthorized error by clearing the current session token
         * and performing a redirect to the login page. If specific methods are defined
         * for clearing tokens or redirecting, these will be utilized; otherwise, a
         * default redirection is performed.
         *
         * @return {void} This function does not return a value.
         */
        function handle401() {
            if (typeof clearCachedUser === 'function') clearCachedUser();
            if (typeof hardRedirectToLogin === 'function') {
                hardRedirectToLogin();
            } else {
                window.location.assign(window.WayGenAI?.loginUrl || '/user/log-in/');
            }
        }

        /**
         * Sends a JSON object as a POST request to the specified URL and handles the response.
         *
         * @param {string} url The URL to send the POST request to.
         * @param {object} [body] The JSON object to include as the body of the request. Defaults to an empty object if not provided.
         * @return {Promise<object>} A promise that resolves to the JSON-parsed response data if the request is successful or rejects with an error if the request fails.
         */
        async function postJSON(url, body) {
            try {
                const resp = await fetch(url, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    credentials: 'include',
                    body: JSON.stringify(body || {})
                });
                if (resp.status === 401) {
                    handle401();
                    throw new Error('Unauthorized');
                }
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok) throw new Error(data?.detail || data?.error || ('HTTP ' + resp.status));
                return data;
            } catch (err) {
                throw err;
            }
        }

        const planToType = {free: null, path: 4, trail: 10};
        const titleByKey = {free: I18N.free, path: I18N.path, trail: I18N.trail};

        let currentKey = 'free';
        let currentCredits = '<1';
        if (!user.subscription.is_free) {
            if (Number(user.subscription.subscription_type) === 4) currentKey = 'path';
            else if (Number(user.subscription.subscription_type) === 10) currentKey = 'trail';
        }
        if (user.subscription.max_allowed_spend && user.subscription.max_allowed_spend >= 1) {
            currentCredits = parseFloat(user.subscription.max_allowed_spend);
            currentCredits = `≈${Math.round(currentCredits)}`;
        }

        $('#wg-plan-current-name').text(titleByKey[currentKey] || '—');
        $('#wg-plan-current-credits').find('span').text(currentCredits);

        if (user.subscription.subscription_ends) {
            const raw = String(user.subscription.subscription_ends);
            const d = new Date(/\dZ$/.test(raw) ? raw : raw.replace(/Z?$/, 'Z'));
            const label = I18N.periodUntil.replace('%s', d.toLocaleDateString());
            $('#wg-plan-period').html(label);
        } else {
            $('#wg-plan-period').html('');
        }

        /**
         * Opens the billing portal for the user by making a request to the server
         * and redirecting the browser to the returned URL if available.
         * Disables the provided button and shows a loading state during the process.
         *
         * @param {jQuery} $btn - The button element that triggers the billing portal opening.
         *                         It will be temporarily disabled and given a loading state.
         * @return {Promise<void>} A promise that resolves when the process completes.
         *                         Redirects the browser based on the returned URL or shows an alert on failure.
         */
        async function openBillingPortal($btn) {
            try {
                if ($btn && $btn.length) $btn.prop('disabled', true).addClass('is-loading');
                const data = await postJSON(API_BASE + '/billing-portal', {});
                if (data?.url) {
                    window.location.href = data.url;
                } else {
                    alert(I18N.unableOpenBilling);
                }
            } catch (e) {
                alert(I18N.unableOpenBilling);
            } finally {
                if ($btn && $btn.length) $btn.prop('disabled', false).removeClass('is-loading');
            }
        }

        const hasStripeCustomer = !!user.subscription.stripe_customer_id;
        const $manageBtn = $('#wg-manage-billing');
        if (!user.subscription.is_free || hasStripeCustomer) {
            $manageBtn.removeClass('is-hidden');
            $manageBtn.off('click.wgManage').on('click.wgManage', function () {
                openBillingPortal($manageBtn);
            });
        } else {
            $manageBtn.addClass('is-hidden').off('click.wgManage');
        }

        // subscribe/upgrade
        $('#wg-plan-cards .pricing-card').each(function () {
            const $card = $(this);
            const planKey = $card.data('plan'); // 'free' | 'path' | 'trail'
            const $btn = $card.find('.plan-btn');

            $card.removeClass('is-current');
            $btn.prop('disabled', false).data('action', '').text(I18N.upgrade).data('planKey', '');

            if (planKey === currentKey) {
                $card.addClass('is-current');
                if (currentKey === 'free') {
                    $btn.prop('disabled', true).data('action', 'current').text(I18N.current);
                } else {
                    $btn.prop('disabled', false).data('action', 'manage').text(I18N.manage);
                }
                return;
            }

            const order = {free: 0, path: 1, trail: 2};
            const isUpgrade = (order[planKey] > order[currentKey]);

            const hasStripeCustomer = !!user.subscription.stripe_customer_id;
            const mustManageInPortal = (!user.subscription.is_free) || hasStripeCustomer;

            if (isUpgrade) {
                if (mustManageInPortal) {
                    $btn.data('action', 'manage').text(I18N.upgrade).data('planKey', '');
                } else {
                    $btn.data('action', 'upgrade').text(I18N.upgrade);
                    const type = planToType[planKey];
                    const planApiKey = I18N.links?.[type] || (planKey === 'path' ? 'pathfinder' : planKey === 'trail' ? 'trailblazer' : '');
                    $btn.data('planKey', planApiKey);
                }
            } else {
                $btn.data('action', 'manage').text(I18N.downgrade);
            }
        });

        $('#wg-plan-cards').off('click.planBtn').on('click.planBtn', '.plan-btn', async function () {
            const $b = $(this);
            const action = $b.data('action');
            if (!action || action === 'current') return;

            if (action === 'manage') {
                openBillingPortal($b);
                return;
            }

            if (action === 'upgrade') {
                const planKey = ($b.data('planKey') || '').trim();
                if (!planKey) {
                    alert(I18N.noUpgradePlan);
                    return;
                }
                try {
                    $b.prop('disabled', true).addClass('is-loading');
                    const data = await postJSON(API_BASE + '/checkout/subscribe', {plan_key: planKey});
                    if (data?.url) {
                        window.location.href = data.url;
                    } else {
                        alert(I18N.unableCheckoutSession);
                    }
                } catch (e) {
                    alert(I18N.unableCheckoutSession);
                } finally {
                    $b.prop('disabled', false).removeClass('is-loading');
                }
            }
        });

        /**
         * Retrieves the top-up application mode based on the selected input.
         *
         * The method first checks for the presence and value of a dropdown element with the id `#wg-topup-mode`.
         * If the dropdown is present and its value is either 'extend' or 'period' (case insensitive), that value is returned.
         * Otherwise, it checks for a selected radio button with the name `wg_topup_mode`, and similarly,
         * returns 'extend' or 'period' based on the selected value.
         * If neither the dropdown nor the radio button is available, the default value 'period' is returned.
         *
         * @return {string} The top-up application mode. Possible values are:
         *                  - 'extend'
         *                  - 'period'
         *                  Default value is 'period'.
         */
        function getTopupApplyMode() {
            const $sel = $('#wg-topup-mode');
            if ($sel.length) {
                const v = String($sel.val() || '').toLowerCase();
                return (v === 'extend' || v === 'period') ? v : 'period';
            }
            const $radio = $('input[name="wg_topup_mode"]:checked');
            if ($radio.length) {
                const v = String($radio.val() || '').toLowerCase();
                return (v === 'extend' || v === 'period') ? v : 'period';
            }
            return 'period';
        }

        /**
         * Renders a list of top-up buttons based on predefined top-up amounts.
         * The method dynamically creates and appends buttons to a DOM element
         * with the ID 'wg-topup-list'. Each button represents a top-up amount
         * and includes the amount as a data attribute.
         *
         * @return {void} This method does not return a value.
         */
        (function renderTopup() {
            const $list = $('#wg-topup-list');
            if (!$list.length) return;
            $list.empty();

            const topupAmounts = Array.isArray(I18N.links.topup) ? I18N.links.topup.slice() : [];
            topupAmounts.sort((a, b) => Number(a) - Number(b));

            topupAmounts.forEach(amount => {
                const label = '€' + String(amount);
                const $btn = $('<button type="button" class="btn btn-topup"></button>')
                    .text(label)
                    .data('amount', Number(amount));
                $list.append($btn);
            });
        })();

        /**
         * Executes a top-up operation by sending a request to the server and handles UI updates during the process.
         * If successful, the user is redirected to a specified URL; if not, an alert is displayed.
         *
         * @param {number} amount The amount to top up.
         * @param {jQuery} $btn The button element that triggers the top-up process; it will be disabled during the operation.
         * @return {Promise<void>} A promise that resolves when the top-up operation completes.
         */
        async function runTopup(amount, $btn) {
            try {
                $btn.prop('disabled', true).addClass('is-loading');
                const apply_mode = getTopupApplyMode();
                const data = await postJSON(API_BASE + '/checkout/topup', {amount, apply_mode});
                if (data?.url) {
                    window.location.href = data.url;
                } else {
                    alert(I18N.unableCheckoutSession);
                }
            } catch (e) {
                alert(I18N.unableCheckoutSession);
            } finally {
                $btn.prop('disabled', false).removeClass('is-loading');
            }
        }

        // init topup
        $('#wg-topup-list').off('click.topup').on('click.topup', '.btn-topup', function () {
            const $b = $(this);
            const amount = Number($b.data('amount') || 0);
            if (!amount) return;
            runTopup(amount, $b);
        });

        $('[data-open-topup]').on('click', () => $('#wg-topup-modal').addClass('is-open'));
        $('[data-close-topup]').on('click', () => $('#wg-topup-modal').removeClass('is-open'));
        $('#wg-topup-modal').on('click', function (e) {
            if (e.target === this) $(this).removeClass('is-open');
        });

        // check reload
        if (window.location.search.includes('reload')) {
            const u = new URL(location.href);
            u.searchParams.delete('reload');
            location.replace(u.toString());
        }
    }
})(jQuery, window, document);
